import { useEffect, useMemo, useState } from "react";
import { Plus, Minus, Trash2, ArrowRight, Move, Receipt, Printer, Send, ListChecks, CheckSquare, Square, X as XIcon, ChevronDown, ChevronRight, Search, Keyboard, RotateCcw, Pencil, ChefHat, Undo2, Euro, Wrench, HandCoins, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { toastFiscalOk, toastFiscalFail } from "@/lib/fiscalToast";
import { http, fmtEUR, warnZeroStock } from "@/lib/api";
// `printProformaHtml` (browser-print path) was removed in favour of the
// backend's ESC/POS `raw_hex` queue → no longer imported. `printKitchenTicket`
// is still imported because legacy callers (corrections, transfer notices)
// haven't been migrated yet.
import { printKitchenTicket } from "@/lib/kitchenTicket";
import { useNavGuard } from "@/lib/NavGuardContext";
import { useTablesStream, mergeServerTables } from "@/hooks/useTablesStream";
import { PaymentDialogBody, CartRow } from "@/pages/POSRetail";
import ProductCatalogPanel from "@/components/ProductCatalogPanel";
import CommentInlinePanel from "@/components/CommentInlinePanel";
import SimpleCommentDialog from "@/components/SimpleCommentDialog";
import ContinuePaymentDialog from "@/components/ContinuePaymentDialog";
import QuickNoteDialog from "@/components/QuickNoteDialog";
import { TableGridFilters } from "@/components/TableGridFilters";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import ConfirmDialog from "@/components/ConfirmDialog";
import Numpad from "@/components/Numpad";
import OnScreenKeyboard from "@/components/OnScreenKeyboard";
import OpenTablesCheckPanel from "@/components/OpenTablesCheckPanel";
import ServingScreen from "@/components/ServingScreen";
import { useAuth, usePermissions } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";

export default function Restaurant() {
  const { setDirty } = useNavGuard();
  const { can, guard } = usePermissions();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [settings, setSettings] = useState(null);
  const [tables, setTables] = useState([]); // [{id, position, device_number, name, department, color, opened, items}]
  const [sections, setSections] = useState([]);
  // Tables grid filter state — single section radio + multi-select status
  // chips. Both default to "all" / [] which shows every table regardless
  // of status; this is the screen the operator sees on every entry per
  // the spec («σαν DEFAULT … ΟΛΑ ΤΑ ΤΡΑΠΕΖΙΑ ΚΑΙ ΟΛΕΣ ΟΤΙ ΚΑΤΑΣΤΑΣΗ»).
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [activeStatuses, setActiveStatuses] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState(null);
  // Highlights the most recently-used table for 5 seconds after a
  // successful submit/send so the operator can spot the last table
  // they interacted with at a glance (e.g. to undo a mistake).
  const [recentlyUsedTableId, setRecentlyUsedTableId] = useState(null);
  const markRecentlyUsed = (tid) => {
    if (!tid) return;
    setRecentlyUsedTableId(tid);
    setTimeout(() => {
      setRecentlyUsedTableId((cur) => (cur === tid ? null : cur));
    }, 5000);
  };
  const [transferMode, setTransferMode] = useState(false);
  const [transferSourceId, setTransferSourceId] = useState(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closePayments, setClosePayments] = useState([]);
  const [closeComment, setCloseComment] = useState("");
  const [closeNonFiscal, setCloseNonFiscal] = useState(false);
  const [printOverride, setPrintOverride] = useState("");
  const [submitChoiceOpen, setSubmitChoiceOpen] = useState(false);
  const [partialOpen, setPartialOpen] = useState(false);
  // Per-line requested send quantity for «Μερική Αποστολή». Keys are
  // ``<batch_no>:<item_idx>``; values are the qty to send (0 = skipped).
  // Lines absent from the map are skipped. When qty < item.qty the backend
  // splits the line on commit.
  const [partialQty, setPartialQty] = useState(() => new Map());
  const [selectedItemIdx, setSelectedItemIdx] = useState(null);
  const [batchesExpanded, setBatchesExpanded] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [comments, setComments] = useState([]);
  const [commentGroups, setCommentGroups] = useState([]);
  const [commentDialog, setCommentDialog] = useState({ open: false, idx: null, submittedKey: null, selections: {}, prefixes: {}, activePrefix: null, freeText: "" });
  const [oskOpen, setOskOpen] = useState(false);
  // Phase 1 Order Corrections — when the operator taps an already-submitted
  // batch item, we collect pending edits in this map (keyed by
  // "<batch_no>:<item_idx>"). Each value carries any subset of qty / price /
  // discount_percent / comments / free_text / modifier_total plus an optional
  // ``deleted: true`` flag. The submitted rows render with corrected values
  // overlaid (amber for edits, red+strikethrough for deletes) until the
  // operator either confirms the corrections (POST /apply-corrections) or
  // cancels them. Phase 2/3 will layer FTEXT + kitchen ticket routing on top
  // of the same state.
  const [corrections, setCorrections] = useState({});
  const [selectedSubmittedKey, setSelectedSubmittedKey] = useState(null);

  // ----- Μερική Πληρωμή (partial payment) state -----
  // The flow has 3 stages: PICK items → optional WARN (for items not yet
  // sent to the cash register) → PAY (open payment dialog with the
  // chosen items only). Items selected here get a ``paid_partial: true``
  // flag in the DB which renders with a strikethrough + yellow stripe
  // in the cart and is skipped from the final close totals.
  const [partialPayOpen, setPartialPayOpen] = useState(false);
  const [partialPayQty, setPartialPayQty] = useState(new Map());
  const [partialPaySplit, setPartialPaySplit] = useState([]);
  const [partialPayComment, setPartialPayComment] = useState("");
  const [partialPayStage, setPartialPayStage] = useState("pick"); // pick | warn | pay
  const [partialPayCashOnly, setPartialPayCashOnly] = useState(false);
  const [partialPaySendUnsent, setPartialPaySendUnsent] = useState(true);

  // ----- ΔΙΑΓΡΑΦΗ ΤΡΑΠΕΖΙΟΥ (Delete Table) state -----
  // ``deleteTableConfirm`` shows the «είστε σίγουρος;» dialog before
  // running the full void+close+reopen+close cycle. ``deleteTableLastItem``
  // is set when the operator's pending deletes would empty the table —
  // we warn them that the next submit will trigger the full delete-table
  // sequence (instead of leaving an empty open table).
  const [deleteTableConfirm, setDeleteTableConfirm] = useState(false);
  const [deleteTableLastItem, setDeleteTableLastItem] = useState(false);
  // Ιούν 2026 — force-delete τραπεζιού που έχει σταλεί στην ταμειακή:
  // προειδοποίηση + numpad κωδικού διαχειριστή.
  const [deleteForce, setDeleteForce] = useState({ open: false, code: "" });
  // Ιούν 2026 — οθόνη Σερβιρίσματος (runner board).
  const [servingOpen, setServingOpen] = useState(false);

  // Quick-prefix toolbar used to qualify the NEXT plain-comment click.
  // When a prefix is active (e.g. ΧΩΡΙΣ), it lights up red until the user
  // taps a comment tile — the comment is then stored with the prefix glued
  // in front of its name (e.g. «ΧΩΡΙΣ ΠΑΓΟ»). Tapping the same tile again
  // toggles the whole entry (prefix + name) off.
  const COMMENT_PREFIXES = ["ΜΕ", "ΧΩΡΙΣ", "ΛΙΓΟ", "ΠΟΛΥ", "ΜΟΝΟ", "EXTRA"];
  const [numpad, setNumpad] = useState({
    open: false,
    target: null, // {kind: "qty"|"price"|"disc", idx}
    label: "",
    value: "",
    allowDot: true,
  });
  const openNumpad = (target, label, initial = "", allowDot = true) =>
    setNumpad({ open: true, target, label, value: String(initial ?? ""), allowDot });
  const closeNumpad = () => setNumpad((n) => ({ ...n, open: false }));
  const setNumpadValue = (v) => setNumpad((n) => ({ ...n, value: v }));
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    const [p, pm, c, s, t, pr, cm, cg, sec] = await Promise.all([
      http.get("/products?channel=restaurant"),
      http.get("/payments"),
      http.get("/categories"),
      http.get("/settings"),
      http.get("/tables"),
      http.get("/printers"),
      http.get("/comments"),
      http.get("/comment-groups"),
      http.get("/restaurant-sections"),
    ]);
    setProducts(p.data.filter((x) => x.is_active));
    setPayments(pm.data);
    setCategories(c.data);
    setSettings(s.data);
    setTables(t.data);
    setPrinters(pr.data || []);
    setComments((cm.data || []).filter((x) => x.is_active));
    setCommentGroups((cg.data || []).filter((x) => x.is_active));
    setSections(sec.data || []);
  };

  // Persist open_items to backend (debounced).
  //
  // Feb 2026 — the cart lives PURELY in local React state during item
  // add/edit/delete: we no longer PUT ``/open-items`` on every keystroke.
  // A table is considered "occupied" on other terminals ONLY after the
  // operator commits via Αποστολή/Αποθήκευση. This helper is retained
  // for the explicit CLEAR-cart flow (cancelAndReturn) where we DO
  // want the server-side state wiped and the change broadcast.
  const persistOpenItems = async (tableId, items) => {
    try {
      await http.put(`/tables/${tableId}/open-items`, { items });
    } catch {
      /* network blip — local state still updated, will sync next time */
    }
  };

  useEffect(() => {
    const t = setTimeout(loadAll, 0);
    return () => clearTimeout(t);
  }, []);

  // Feb 2026 — subscribe to /tables/stream so every state change (order,
  // release, add-items, transfer) done on ANOTHER terminal (PC or mobile)
  // is reflected in this grid within ≈100 ms. See useTablesStream for the
  // full contract; ``reloadTables`` handles bulk resets.
  const reloadTables = async () => {
    try {
      const r = await http.get("/tables");
      // merge-safe: μη σβήνεις το τοπικό αστάλτο cart (βλ. useTablesStream)
      setTables((prev) => mergeServerTables(prev, r.data || []));
    } catch { /* keep local state */ }
  };
  useTablesStream({ setTables, onBulkChange: reloadTables });

  // Ιούν 2026 — safety poll 30s: αν χαθεί SSE event σε reconnect gap,
  // το ταμπλό αυτο-διορθώνεται χωρίς F5 (merge-safe, δεν χάνεται cart).
  useEffect(() => {
    const safety = setInterval(reloadTables, 30000);
    return () => clearInterval(safety);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tick the clock every 30 s so the table-grid elapsed-time labels stay
  // fresh without re-fetching the whole table list. Pure local UI state.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Recently-paid table IDs — held for ~5s after a successful close so the
  // grid flashes a «✓ Πληρωμένο» badge before the table snaps back to its
  // ΕΛΕΥΘΕΡΟ green state. The Set is the source of truth; we re-render
  // by mutating it and calling setRecentlyPaid with a fresh copy.
  const [recentlyPaid, setRecentlyPaid] = useState(() => new Set());
  const markRecentlyPaid = (id, ms = 5000) => {
    setRecentlyPaid((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setTimeout(() => {
      setRecentlyPaid((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, ms);
  };

  // ───────── Transient-UI reset ─────────
  // Single source of truth for clearing any per-table interaction state
  // that should NOT survive a table switch. Called from the
  // ``selectedTableId`` effect plus any handler that explicitly returns
  // to the grid. Keeping this in one place protects against new panels
  // being added later that forget to reset themselves on navigation.
  const resetTransientUI = () => {
    setBatchesExpanded(false);
    setCorrections({});
    setSelectedSubmittedKey(null);
    setSelectedItemIdx(null);
    setCommentDialog({ open: false, idx: null, submittedKey: null, selections: {}, prefixes: {}, activePrefix: null, freeText: "" });
    setOskOpen(false);
    setNumpad((n) => ({ ...n, open: false }));
    setSubmitChoiceOpen(false);
    setPartialOpen(false);
    setPartialQty(new Map());
    setCancelConfirmOpen(false);
    setCloseOpen(false);
    setClosePayments([]);
    setCloseComment("");
    setCloseNonFiscal(false);
    setPrintOverride("");
    setReprintPreview({ open: false, batchNo: null, tickets: [], loading: false });
    setSearch("");
    setActiveCat("all");
  };

  // Collapse previous batches whenever a different table is opened.
  useEffect(() => {
    resetTransientUI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTableId]);

  // Navigation guard: block sidebar nav while the current table has unsent items.
  const openCount = tables.find((t) => t.id === selectedTableId)?.open_items?.length || 0;
  useEffect(() => {
    setDirty(
      openCount > 0,
      "Ολοκλήρωσε την παραγγελία ή πάτησε Ακύρωση & Επιστροφή"
    );
    return () => setDirty(false);
  }, [openCount, setDirty]);

  const updateTable = (id, patch) =>
    setTables((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));

  const tbl = useMemo(
    () => tables.find((t) => t.id === selectedTableId),
    [tables, selectedTableId]
  );

  const totals = useMemo(() => {
    if (!tbl) return { open: 0, submitted: 0, total: 0, correctionDelta: 0 };
    const open = (tbl.open_items || []).reduce((s, it) => {
      const gross =
        Number(it.qty || 0) * (Number(it.price || 0) + Number(it.modifier_total || 0));
      // Ιούν 2026: η έκπτωση γραμμής μετράει και στο σύνολο (bug: αγνοούνταν)
      return s + (it.discount_percent ? gross * (1 - Number(it.discount_percent) / 100) : gross);
    }, 0);
    // Recompute submitted totals taking into account any pending
    // corrections so the operator sees a live preview at the bottom.
    let submitted = 0;
    let baseline = 0;
    for (const b of tbl.submitted_batches || []) {
      for (let i = 0; i < (b.items || []).length; i += 1) {
        const orig = b.items[i];
        // Committed deletions stay in history but never count toward the
        // table total — they're the "ήταν" reference, not the receipt.
        if (orig.corrected_deleted) continue;
        // Same for items settled via μερική πληρωμή — they're already paid
        // and counted in their own retail receipt, must not be double-billed.
        if (orig.paid_partial) continue;
        const baseUnit = Number(orig.price || 0) + Number(orig.modifier_total || 0);
        const baseGross = Number(orig.qty || 0) * baseUnit;
        const baseNet = orig.discount_percent
          ? baseGross * (1 - Number(orig.discount_percent) / 100)
          : baseGross;
        baseline += baseNet;
        const key = `${b.batch_no}:${i}`;
        const c = corrections[key];
        if (c?.deleted) continue;
        const eff = c ? { ...orig, ...c } : orig;
        const unit = Number(eff.price || 0) + Number(eff.modifier_total || 0);
        const gross = Number(eff.qty || 0) * unit;
        submitted += eff.discount_percent
          ? gross * (1 - Number(eff.discount_percent) / 100)
          : gross;
      }
    }
    return {
      open,
      submitted,
      total: open + submitted,
      correctionDelta: submitted - baseline,
    };
  }, [tbl, corrections]);

  const hasSession = !!(tbl && ((tbl.open_items || []).length > 0 || (tbl.submitted_batches || []).length > 0));

  const addProduct = (p) => {
    if (!tbl) return;
    warnZeroStock(p);
    const items = [...(tbl.open_items || [])];
    // Items that allow comments are always distinct rows (qty=1) so each
    // can carry its own modifier selection.
    const existing = !p.allow_comment
      ? items.findIndex(
          (it) => it.description === p.description && it.price === p.price && !it.comment && !it.allow_comment,
        )
      : -1;
    let newIdx;
    if (existing >= 0) {
      items[existing] = { ...items[existing], qty: Number(items[existing].qty) + 1 };
      newIdx = existing;
    } else {
      items.push({
        description: p.description,
        qty: 1,
        price: p.price,
        vat_rate: p.vat_rate,
        department: p.department,
        product_id: p.id,
        allow_comment: !!p.allow_comment,
        comment_group_ids: p.comment_group_ids || [],
        comments: [],
        comment: "",
        modifier_total: 0,
      });
      newIdx = items.length - 1;
    }
    updateTable(tbl.id, { open_items: items });
    // Feb 2026 — do NOT persist here; the cart stays local until the
    // operator commits via Αποστολή/Αποθήκευση (see persistAsBatch).
    setSelectedItemIdx(newIdx);
    setSelectedSubmittedKey(null);
    if (p.allow_comment && p.auto_open_comment) {
      const groupIds = p.comment_group_ids || [];
      if (groupIds.length === 0) {
        setSimpleComment({ open: true, idx: newIdx, source: "open" });
      } else {
        const existing = items[newIdx].comments || [];
        const sel = {};
        const pref = {};
        existing.forEach((c) => {
          sel[c.id] = c.quantity || 1;
          if (c.prefix) pref[c.id] = c.prefix;
        });
        setCommentDialog({ open: true, idx: newIdx, submittedKey: null, selections: sel, prefixes: pref, activePrefix: null, freeText: items[newIdx].free_text || "" });
      }
    }
  };

  // Free-text dialog (large pop-up) for items without comment groups.
  // ``source`` distinguishes open_items vs submittedKey edit-mode.
  const [simpleComment, setSimpleComment] = useState({ open: false, idx: null, source: "open" });
  const openCommentDialog = (idx) => {
    if (!tbl) return;
    const it = (tbl.open_items || [])[idx];
    if (!it) return;
    const groupIds = it.comment_group_ids || [];
    if (groupIds.length === 0) {
      setSimpleComment({ open: true, idx, source: "open" });
      return;
    }
    const sel = {};
    const pref = {};
    (it.comments || []).forEach((c) => {
      sel[c.id] = c.quantity || 1;
      if (c.prefix) pref[c.id] = c.prefix;
    });
    setCommentDialog({ open: true, idx, submittedKey: null, selections: sel, prefixes: pref, activePrefix: null, freeText: it.free_text || "" });
  };

  const _formatCommentLabel = (c, qty, prefix) => {
    if (qty === -1) return `ΧΩΡΙΣ ${c.name}`;
    if (qty > 1) return prefix ? `${qty}× ${prefix} ${c.name}` : `${qty}× ${c.name}`;
    return prefix ? `${prefix} ${c.name}` : c.name;
  };

  const saveComment = () => {
    // BUG FIX (Feb 2026): για submitted-item corrections, το ``idx`` είναι
    // εσκεμμένα ``null`` και το ``submittedKey`` σηματοδοτεί ποιο υπάρχον
    // item επεξεργαζόμαστε. Ο παλιός guard ``if (idx == null) return`` έκοβε
    // ΟΛΕΣ τις αλλαγές σχολίων σε καταχωρημένα είδη → ο χειριστής νόμιζε
    // ότι έκανε save αλλά τίποτα δεν είχε γραφτεί στο ``corrections`` state.
    if (commentDialog.idx == null && !commentDialog.submittedKey) return;
    const out = [];
    let total = 0;
    Object.entries(commentDialog.selections).forEach(([cid, qty]) => {
      const c = comments.find((x) => x.id === cid);
      if (!c || qty === 0) return;
      const effectiveQty = Number(qty) || 0;
      const prefix = commentDialog.prefixes?.[cid] || null;
      out.push({
        id: c.id,
        name: c.name,
        label: _formatCommentLabel(c, effectiveQty, prefix),
        prefix,
        price_delta: c.price_delta,
        is_deduction: !!c.is_deduction,
        color: c.color,
        radio_group: c.radio_group,
        splittable: c.splittable,
        quantity: effectiveQty,
      });
      // is_deduction inverts the contribution (e.g. «Χωρίς τυρί» → -1€).
      // For splittable in «ΧΩΡΙΣ» state (qty=-1) the comment is treated as a
      // deduction regardless of the flag.
      const sign = c.is_deduction ? -1 : 1;
      if (effectiveQty > 0) total += sign * Number(c.price_delta || 0) * effectiveQty;
      else if (effectiveQty === -1 && c.is_deduction) total -= Number(c.price_delta || 0);
    });
    const freeText = (commentDialog.freeText || "").trim();
    const labels = out.map((c) => c.label);
    if (freeText) labels.push(freeText);
    // ----- Submitted item correction path -----
    if (commentDialog.submittedKey) {
      setCorrection(commentDialog.submittedKey, {
        comments: out,
        comment: labels.join(", "),
        free_text: freeText || null,
        modifier_total: total,
      });
      setCommentDialog({ open: false, idx: null, submittedKey: null, selections: {}, prefixes: {}, activePrefix: null, freeText: "" });
      return;
    }
    updateOpenItem(commentDialog.idx, {
      comments: out,
      comment: labels.join(", "),
      free_text: freeText || null,
      modifier_total: total,
    });
    setCommentDialog({ open: false, idx: null, submittedKey: null, selections: {}, prefixes: {}, activePrefix: null, freeText: "" });
  };

  const toggleCommentTile = (c) => {
    setCommentDialog((d) => {
      const sel = { ...d.selections };
      const pref = { ...(d.prefixes || {}) };
      if (sel[c.id]) {
        // Toggle off – removes the whole entry including any prefix.
        delete sel[c.id];
        delete pref[c.id];
        return { ...d, selections: sel, prefixes: pref };
      }
      if (c.radio_group && !c.radio_multiple) {
        // Radio behavior — clear other NON-splittable selections of the same
        // radio_group only. Splittable siblings keep their quantity so a
        // «2× ζάχαρη» can coexist with «1× γάλα» under the same group.
        // When ``radio_multiple`` is true on the subgroup we skip clearing
        // entirely — the user is allowed to pick multiple options.
        comments
          .filter(
            (x) =>
              x.group_id === c.group_id &&
              x.radio_group === c.radio_group &&
              !x.splittable,
          )
          .forEach((x) => {
            delete sel[x.id];
            delete pref[x.id];
          });
      }
      sel[c.id] = 1;
      // Consume the active prefix (if any) and stamp it onto this comment.
      const active = d.activePrefix;
      if (active) pref[c.id] = active;
      return { ...d, selections: sel, prefixes: pref, activePrefix: null };
    });
  };

  const togglePrefix = (p) => {
    setCommentDialog((d) => ({
      ...d,
      activePrefix: d.activePrefix === p ? null : p,
    }));
  };

  // Splittable tile handlers: + increments (1→2→3…), − decrements (3→2→1→absent→-1 «ΧΩΡΙΣ»; −1 stops).
  const incrementSplit = (c) => {
    setCommentDialog((d) => {
      const sel = { ...d.selections };
      const cur = sel[c.id] || 0;
      if (cur === -1) delete sel[c.id]; // από «ΧΩΡΙΣ» το + πάει στο 0 (χωρίς χρέωση)
      else sel[c.id] = cur + 1;
      return { ...d, selections: sel };
    });
  };
  const decrementSplit = (c) => {
    setCommentDialog((d) => {
      const sel = { ...d.selections };
      const cur = sel[c.id] || 0;
      if (cur === 0) sel[c.id] = -1; // ΧΩΡΙΣ
      else if (cur === 1) delete sel[c.id];
      else if (cur === -1) {/* stop at ΧΩΡΙΣ */}
      else sel[c.id] = cur - 1;
      return { ...d, selections: sel };
    });
  };

  // ---- Open-items helpers (Retail-style: numpad-driven qty/price/disc) ----
  const updateOpenItem = (idx, patch) => {
    if (!tbl) return;
    const items = [...(tbl.open_items || [])];
    if (!items[idx]) return;
    items[idx] = { ...items[idx], ...patch };
    updateTable(tbl.id, { open_items: items });
    // Feb 2026 — local-only cart; no PUT until submit.
  };

  const removeOpenItem = (idx) => {
    if (!tbl) return;
    const items = (tbl.open_items || []).filter((_, i) => i !== idx);
    updateTable(tbl.id, { open_items: items });
    // Feb 2026 — local-only cart; no PUT until submit.
    setSelectedItemIdx(null);
  };

  // ---- Phase 1 Order Corrections helpers ----
  // Returns the effective item (original + pending patch) so renderers can
  // show the corrected qty/price/comment/discount without mutating the
  // persisted batch yet.
  const getEffectiveBatchItem = (batchNo, itemIdx, original) => {
    const key = `${batchNo}:${itemIdx}`;
    const c = corrections[key];
    if (!c) return { item: original, corrected: false, deleted: false, key };
    const { deleted, ...patch } = c;
    return {
      item: { ...original, ...patch },
      corrected: true,
      deleted: !!deleted,
      key,
      correction: c,
    };
  };

  const setCorrection = (key, patch) => {
    setCorrections((prev) => {
      const next = { ...prev };
      next[key] = { ...(next[key] || {}), ...patch };
      return next;
    });
  };

  const clearCorrection = (key) => {
    setCorrections((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const correctionsCount = useMemo(() => Object.keys(corrections).length, [corrections]);

  const cancelAllCorrections = () => {
    setCorrections({});
    setSelectedSubmittedKey(null);
  };

  // Print proforma («ΛΟΓΑΡΙΑΣΜΟΣ») to the default printer. Does NOT mutate
  // any state; the operator can call this repeatedly. After success we
  // stay on the same view (per user spec).
  const printProforma = async () => {
    if (!tbl) return;
    setBusy(true);
    try {
      // Backend writes the .proforma.txt to the FTEXT outbox AND enqueues
      // a print_job with `raw_hex` for the Companion v3 engine on the POS
      // PC — that path already produces a desktop-grade ESC/POS receipt
      // with business header + inline CODE128 barcode + single cut. The
      // queue print is the single source of truth; we deliberately do NOT
      // also fire `printProformaHtml` here because it would produce a
      // duplicate paper slip (one Windows GDI render + one FText v3
      // render).
      const r = await http.post(`/tables/${tbl.id}/print-proforma`);
      const printerName = r.data?.printer_name;
      const grandTotal = Number(r.data?.total || 0);
      toast.success(
        `🖨️ Λογαριασμός εκτυπώθηκε στο ${printerName} · ${fmtEUR(grandTotal)}`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία εκτύπωσης λογαριασμού");
    } finally {
      setBusy(false);
    }
  };

  // Pre-close confirmation: if the table has UNSENT items mixed with
  // fiscal lines, the operator must explicitly choose to flush them
  // before the final receipt. Pressing «Κλείσιμο» first runs this check.
  const [pendingFlushOpen, setPendingFlushOpen] = useState(false);
  // Opt-in: print the proforma after the flush but before the final
  // receipt — useful so the customer can review the bill on paper before
  // payment. Persists per-session (resets when the dialog closes).
  const [flushPrintProforma, setFlushPrintProforma] = useState(false);
  // After «Άμεση Αποστολή & Έκδοση Τελικής», keep the # of items just
  // flushed so the Payment dialog can flash a success badge above the
  // payment selector. Auto-clears when the dialog closes.
  const [recentlyFlushed, setRecentlyFlushed] = useState(0);
  // «ΣΥΝΕΧΙΣΗ ΕΙΣΠΡΑΞΗΣ» (AA / BB cash-drawer split) — opens the
  // ContinuePaymentDialog so the operator can settle the fiscal portion
  // on ΑΑ and / or save the kitchen-only portion to ΒΒ.
  const [continuePayOpen, setContinuePayOpen] = useState(false);
  const [quickNoteOpen, setQuickNoteOpen] = useState(false);

  const onCloseButtonClick = () => {
    if (!tbl) return;
    const unsent = allUnsentSelections(tbl);
    if (unsent.length > 0) {
      // Show the warning dialog — operator decides whether to flush + close.
      setPendingFlushOpen(true);
    } else {
      // All items are already on the register; jump straight into payment.
      setCloseOpen(true);
    }
  };

  const handleFlushAndClose = async () => {
    if (!tbl) return;
    setBusy(true);
    try {
      // 1) Flush any open_items (persist as batch — uncommon but possible).
      const corrFresh = await _flushCorrections();
      const refreshed = await persistAsBatch();
      const baseTable = refreshed || corrFresh || tbl;
      const unsent = allUnsentSelections(baseTable);
      // 2) Send everything still in standby to the cash register.
      if (unsent.length > 0) {
        await sendToRegister(unsent, baseTable);
        setRecentlyFlushed(unsent.length);
      }
      // 2b) Optional courtesy proforma right after flush — customer sees
      //     the bill before the final receipt is issued.
      if (flushPrintProforma) {
        try {
          await printProforma();
        } catch {
          /* non-fatal — toast already surfaced */
        }
      }
      // 3) Open the payment dialog so the operator picks Cash / Card and
      //    the existing closeTable() finishes the fiscal close.
      setPendingFlushOpen(false);
      setCloseOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα αποστολής στην ταμειακή");
    } finally {
      setBusy(false);
    }
  };

  // Internal helper used by the submit handlers. Returns the response
  // headers so callers can surface print/void counts via toast. Throws on
  // network failure so the caller can fall back / abort the rest of the
  // submit pipeline.
  const _flushCorrections = async () => {
    if (!tbl || correctionsCount === 0) return null;
    const r = await http.post(`/tables/${tbl.id}/apply-corrections`, { corrections });
    updateTable(tbl.id, {
      submitted_batches: r.data.submitted_batches,
      open_items: r.data.open_items,
    });
    const n = correctionsCount;
    const printedN = Number(r.headers?.["x-printed-tickets"] || 0);
    const mods = Number(r.headers?.["x-correction-modifications"] || 0);
    const cancels = Number(r.headers?.["x-correction-cancellations"] || 0);
    const printers = decodeURIComponent(r.headers?.["x-printed-printers"] || "").split(";").filter(Boolean);
    setCorrections({});
    setSelectedSubmittedKey(null);
    toast.success(`Διορθώσεις εφαρμόστηκαν (${n})`);
    if (printedN > 0) {
      const parts = [];
      if (mods > 0) parts.push(`${mods} διόρθωσ${mods === 1 ? "η" : "εις"}`);
      if (cancels > 0) parts.push(`${cancels} ακύρωσ${cancels === 1 ? "η" : "εις"}`);
      toast.success(
        `🖨️ ${printedN} χαρτάκια κουζίνας (${parts.join(" + ")})` +
          (printers.length ? ` → ${[...new Set(printers)].join(", ")}` : ""),
      );
    }
    // Return refreshed table so callers can build follow-up FTEXT payloads
    // against the corrected state (open_items now includes any fiscal
    // replacement lines spawned by MODIFY corrections).
    return r.data;
  };

  // Toggle selection of a submitted batch item. Clears the open-item
  // selection to avoid two simultaneously-active edit toolbars.
  const toggleSubmittedSelection = (key) => {
    setSelectedItemIdx(null);
    setSelectedSubmittedKey((prev) => (prev === key ? null : key));
  };

  // Mark a submitted item as deleted (toggleable) — kept in pending state
  // until the operator confirms via "Καταχώρηση Διορθώσεων".
  const toggleDeleteSubmitted = (key) => {
    setCorrections((prev) => {
      const cur = prev[key] || {};
      if (cur.deleted) {
        // Untoggle the deletion flag; if nothing else was patched the
        // correction disappears entirely.
        const { deleted: _d, ...rest } = cur;
        if (Object.keys(rest).length === 0) {
          const { [key]: _r, ...without } = prev;
          return without;
        }
        return { ...prev, [key]: rest };
      }
      return { ...prev, [key]: { ...cur, deleted: true } };
    });
  };

  // Opens the comment selector for a submitted batch item. Reuses the same
  // CommentInlinePanel via ``commentDialog.submittedKey``; ``saveComment``
  // routes the result into ``corrections`` instead of ``open_items``.
  const openCommentDialogForSubmitted = (key, effectiveItem) => {
    const sel = {};
    const pref = {};
    (effectiveItem.comments || []).forEach((c) => {
      sel[c.id] = c.quantity || 1;
      if (c.prefix) pref[c.id] = c.prefix;
    });
    setCommentDialog({
      open: true,
      idx: null,
      submittedKey: key,
      selections: sel,
      prefixes: pref,
      activePrefix: null,
      freeText: effectiveItem.free_text || "",
    });
  };

  // Numpad apply — mirrors POSRetail.onNumpadAccept for qty/price/disc.
  const onNumpadAccept = (parsed) => {
    const t = numpad.target;
    if (parsed == null || !t) {
      closeNumpad();
      return;
    }
    const { kind, idx, submittedKey } = t;
    // ----- Submitted batch item correction path -----
    if (submittedKey) {
      // Resolve the effective (original + pending) item for discount-cap math.
      const [bnS, iiS] = submittedKey.split(":");
      const bn = Number(bnS);
      const ii = Number(iiS);
      const b = (tbl?.submitted_batches || []).find((x) => x.batch_no === bn);
      const orig = b?.items?.[ii];
      if (!orig) {
        closeNumpad();
        return;
      }
      const eff = getEffectiveBatchItem(bn, ii, orig).item;
      if (kind === "qty") {
        setCorrection(submittedKey, { qty: Math.max(0.01, parsed) });
      } else if (kind === "price") {
        const prodP = products.find((p) => p.id === orig.product_id);
        if (prodP && prodP.allow_price_change === false) {
          toast.error("Δεν επιτρέπεται αλλαγή τιμής σε αυτό το είδος");
          closeNumpad();
          return;
        }
        setCorrection(submittedKey, { price: Math.max(0, parsed) });
      } else if (kind === "disc") {
        let pct = Math.max(0, parsed);
        if (pct === 0) {
          setCorrection(submittedKey, { discount_percent: null });
        } else {
          let capped = Math.min(99, pct);
          const unit = Number(eff.price || 0) + Number(eff.modifier_total || 0);
          const gross = (Number(eff.qty) || 0) * unit;
          if (gross > 0) {
            const maxByNet = ((gross - 0.01) / gross) * 100;
            if (capped > maxByNet) capped = Math.max(0, maxByNet);
          }
          capped = Math.round(capped * 100) / 100;
          if (capped < pct) {
            toast.info(`Έκπτωση προσαρμόστηκε σε ${capped}% (όριο 99%)`);
          }
          setCorrection(submittedKey, { discount_percent: capped > 0 ? capped : null });
        }
      }
      closeNumpad();
      return;
    }
    // ----- Open item path (unchanged) -----
    if (idx == null) {
      closeNumpad();
      return;
    }
    const it = (tbl?.open_items || [])[idx];
    if (!it) {
      closeNumpad();
      return;
    }
    if (kind === "qty") {
      updateOpenItem(idx, { qty: Math.max(0.01, parsed) });
    } else if (kind === "price") {
      const prodP = products.find((p) => p.id === it.product_id);
      if (prodP && prodP.allow_price_change === false) {
        toast.error("Δεν επιτρέπεται αλλαγή τιμής σε αυτό το είδος");
        closeNumpad();
        return;
      }
      updateOpenItem(idx, { price: Math.max(0, parsed) });
    } else if (kind === "disc") {
      let pct = Math.max(0, parsed);
      if (pct === 0) {
        updateOpenItem(idx, { discount_percent: null });
      } else {
        let capped = Math.min(99, pct);
        const gross = (Number(it.qty) || 0) * (Number(it.price) || 0);
        if (gross > 0) {
          const maxByNet = ((gross - 0.01) / gross) * 100;
          if (capped > maxByNet) capped = Math.max(0, maxByNet);
        }
        capped = Math.round(capped * 100) / 100;
        if (capped < pct) {
          toast.info(`Έκπτωση προσαρμόστηκε σε ${capped}% (όριο 99%)`);
        }
        updateOpenItem(idx, { discount_percent: capped > 0 ? capped : null });
      }
    }
    closeNumpad();
  };

  // Step 1: just persist open_items as a new batch (no FTEXT call).
  // Returns the fresh server-side table state, or ``null`` when there are
  // no open_items to persist — callers must then fall back to the most
  // up-to-date snapshot they have (e.g. the response from
  // ``_flushCorrections``) rather than the stale ``tbl`` closure.
  // Open the browser-print dialog for one or more kitchen tickets using
  // the unified TICKETA template (see `lib/kitchenTicket.js`). One slip
  // per assigned printer / station.
  const openKitchenTicketsPrint = async (tickets, batchNo) => {
    if (!tickets || tickets.length === 0) return;
    for (const t of tickets) {
      const items = (t.items || []).map((it) => ({
        qty: Number(it.qty || 1),
        description: it.description,
        comments: it.comments || [],
        free_text: it.free_text || null,
      }));
      printKitchenTicket({
        spot: t.printer_name || "ΕΚΤΥΠΩΤΗΣ",
        module: "RESTAURANT",
        tableName: tbl?.name || (tbl?.position ? `#${tbl.position}` : null),
        orderNo: batchNo ?? t.batch_no ?? null,
        operatorName: user?.name || user?.username || null,
        items,
      });
    }
  };

  const persistAsBatch = async () => {
    const items = tbl?.open_items || [];
    if (items.length === 0) return null;
    // Feb 2026 — cart is local-only until this point. Send the items
    // in the request body so the server never had to hold a "draft"
    // copy visible to other terminals.
    const t = await http.post(`/tables/${tbl.id}/submit-batch`, { items });
    // Ιούν 2026: ανανέωση αποθέματος (stock badges) μετά την καταχώρηση.
    http.get("/products?channel=restaurant")
      .then((r) => setProducts(r.data.filter((x) => x.is_active)))
      .catch(() => {});
    updateTable(tbl.id, {
      open_items: t.data.open_items,
      submitted_batches: t.data.submitted_batches,
      session_opened_at: t.data.session_opened_at,
    });
    // Surface printer dispatch feedback from response headers — tells the
    // operator that kitchen tickets were emitted to which printers.
    const printedN = Number(t.headers?.["x-printed-tickets"] || 0);
    const printers = decodeURIComponent(t.headers?.["x-printed-printers"] || "").split(";").filter(Boolean);
    if (printedN > 0) {
      toast.success(
        `🖨️ Στάλθηκαν ${printedN} χαρτάκια εκτύπωσης` +
          (printers.length ? ` → ${printers.join(", ")}` : ""),
      );
      // NOTE: We deliberately do NOT also call `openKitchenTicketsPrint`
      // here. The backend has already enqueued an ESC/POS `raw_hex` print
      // job per kitchen-routed printer and the Companion v3 poller picks
      // them up — a single, high-fidelity paper slip per printer. Firing
      // the browser-print path on top would produce a duplicate (Windows
      // GDI render) on the same printer.
    }
    return t.data;
  };

  const [reprintPreview, setReprintPreview] = useState({ open: false, batchNo: null, tickets: [], loading: false });

  const reprintBatch = async (batchNo) => {
    if (!tbl) return;
    setReprintPreview({ open: true, batchNo, tickets: [], loading: true });
    try {
      const r = await http.get(`/tables/${tbl.id}/batches/${batchNo}/preview-tickets`);
      setReprintPreview({ open: true, batchNo, tickets: r.data?.tickets || [], loading: false });
    } catch (e) {
      setReprintPreview({ open: false, batchNo: null, tickets: [], loading: false });
      toast.error("Σφάλμα προεπισκόπησης: " + (e?.response?.data?.detail || e.message));
    }
  };

  const confirmReprint = async () => {
    const batchNo = reprintPreview.batchNo;
    if (!tbl || batchNo == null) return;
    try {
      const r = await http.post(`/tables/${tbl.id}/batches/${batchNo}/reprint`);
      const n = r.data?.reprinted || 0;
      const printers = r.data?.printers || [];
      if (n === 0) toast.warning("Καμία εκτύπωση: είδη χωρίς εκτυπωτή ή ανενεργοί εκτυπωτές.");
      else toast.success(`🖨️ Επανεκτύπωση παραγγελίας #${batchNo}: ${n} χαρτάκια → ${printers.join(", ")}`);
      // Also open the browser print dialog for each ticket — same UX
      // as the Delivery proforma so the operator gets a print menu.
      await openKitchenTicketsPrint(reprintPreview.tickets || [], batchNo);
      setReprintPreview({ open: false, batchNo: null, tickets: [], loading: false });
    } catch (e) {
      toast.error("Σφάλμα επανεκτύπωσης: " + (e?.response?.data?.detail || e.message));
    }
  };

  // Step 2: send specific batch items to the cash register via FTEXT,
  // then mark them as sent.
  const sendToRegister = async (selections, refreshedTable) => {
    const tableData = refreshedTable || tbl;
    // Note: selections may be empty when the caller only wants to flush
    // pending fiscal voids (corrected_deleted submitted items). The later
    // ``items.length === 0 && voidLines.length === 0`` check handles the
    // truly-empty case.
    // Build items list from selections — point into refreshed batches.
    const items = [];
    for (const { batch_no, item_idx, qty } of selections) {
      const b = (tableData.submitted_batches || []).find((x) => x.batch_no === batch_no);
      const it = b?.items?.[item_idx];
      if (!it) continue;
      // Skip rows that represent history (committed deletions). They
      // already carry their own VOID_LINE in ``void_lines`` below.
      if (it.corrected_deleted) continue;
      // Skip rows that have already been settled via μερική πληρωμή —
      // the customer paid for them in a separate retail receipt and we
      // already VOID-ed the corresponding fiscal lines on the register.
      if (it.paid_partial) continue;
      // Effective unit price = base price ± modifier_total. The customer pays
      // for extras / receives credit for deductions on the FISCAL receipt.
      // ``comments`` and ``free_text`` are forwarded for the kitchen ticket
      // CMT lines but are stripped from the fiscal printout by the builder.
      const unit = Number(it.price || 0) + Number(it.modifier_total || 0);
      // Partial-qty support: callers may pass ``qty`` per selection. If
      // omitted or >= line total we send the whole line; otherwise the
      // backend ``/mark-items-sent`` will split the line on commit.
      const effQty = qty != null && qty < Number(it.qty) ? Number(qty) : Number(it.qty);
      items.push({
        description: it.description,
        qty: effQty,
        price: Math.max(0, unit),
        vat_rate: it.vat_rate,
        department: it.department,
        comments: it.comments || [],
        free_text: it.free_text || null,
        modifier_total: Number(it.modifier_total || 0),
      });
    }
    // Phase 3 — gather VOID_LINE numbers for fiscal items the operator
    // deleted or modified (modify ≡ delete-old + add-new replacement
    // already inserted into open_items by /apply-corrections).
    const voidLines = [];
    for (const b of tableData.submitted_batches || []) {
      for (const it of b.items || []) {
        if (
          it.corrected_deleted &&
          it.sent_to_register &&
          typeof it.fiscal_line_no === "number" &&
          !it.voided_on_register
        ) {
          voidLines.push(it.fiscal_line_no);
        }
      }
    }
    if (items.length === 0 && voidLines.length === 0) {
      toast.error("Επιλογές μη έγκυρες");
      return;
    }
    // 1) Send to cash register via FTEXT (items + voids in a single REST_TABLE_ORDER)
    const r = await http.post("/ftext/restaurant-order", {
      table_nr: tbl.device_number,
      table_name: tbl.name,
      items,
      void_lines: voidLines,
    });
    if (!r.data.ok) {
      toastFiscalFail();
      return;
    }
    // 2) Mark items as sent_to_register
    let updated = null;
    if (selections.length > 0 && items.length > 0) {
      const t = await http.post(`/tables/${tbl.id}/mark-items-sent`, { selections });
      updated = t.data;
    }
    // 3) Mark fiscal voids as voided_on_register
    if (voidLines.length > 0) {
      const t = await http.post(`/tables/${tbl.id}/mark-voided`, { fiscal_line_nos: voidLines });
      updated = t.data;
    }
    if (updated) {
      updateTable(tbl.id, {
        open_items: updated.open_items,
        submitted_batches: updated.submitted_batches,
      });
    }
    const bits = [];
    if (items.length > 0) bits.push(`${items.length} είδη`);
    if (voidLines.length > 0) bits.push(`${voidLines.length} ακυρώσεις`);
    toast.success(
      `Στάλθηκαν στην ταμειακή: ${bits.join(" + ")}` +
        (r.data.log?.simulator ? " (Simulator)" : ""),
    );
  };

  // Helper: build selection list of every unsent item across all batches.
  const allUnsentSelections = (tableData) => {
    const out = [];
    for (const b of tableData.submitted_batches || []) {
      (b.items || []).forEach((it, i) => {
        // Skip history rows (deleted) and already-sent fiscal lines.
        if (it.corrected_deleted) return;
        if (it.sent_to_register) return;
        out.push({ batch_no: b.batch_no, item_idx: i });
      });
    }
    return out;
  };

  // ----- Partial Payment (Μερική Πληρωμή) -----
  const startPartialPayment = () => {
    if (!tbl) return;
    setPartialPayQty(new Map());
    setPartialPaySplit([]);
    setPartialPayComment("");
    setPartialPayCashOnly(false);
    setPartialPaySendUnsent(true);
    setPartialPayStage("pick");
    setPartialPayOpen(true);
  };

  // ----- ΔΙΑΓΡΑΦΗ ΤΡΑΠΕΖΙΟΥ (Delete Table) -----
  // Detect "last-item void" — true when:
  //   • there ARE pending corrections (the operator is mid-delete);
  //   • applying them would leave the table with zero billable lines
  //     (no remaining non-deleted/non-paid submitted items + no open_items).
  const wouldEmptyTable = useMemo(() => {
    if (!tbl || correctionsCount === 0) return false;
    if ((tbl.open_items || []).length > 0) return false;
    let remaining = 0;
    for (const b of tbl.submitted_batches || []) {
      const bn = b.batch_no;
      (b.items || []).forEach((it, i) => {
        if (it.corrected_deleted) return;
        if (it.paid_partial) return;
        const corr = corrections[`${bn}:${i}`];
        if (corr && corr.deleted) return;
        remaining += 1;
      });
    }
    return remaining === 0;
  }, [tbl, corrections, correctionsCount]);

  // True όταν το τραπέζι έχει είδη ήδη σταλμένα στην ταμειακή (fiscal
  // γραμμές που δεν έχουν ακυρωθεί/πληρωθεί). Τότε η διαγραφή απαιτεί
  // κωδικό διαχειριστή (νέα διαδικασία Ιουν 2026).
  const tableHasFiscal = useMemo(() => {
    if (!tbl) return false;
    return (tbl.submitted_batches || []).some((b) =>
      (b.items || []).some(
        (it) =>
          it.sent_to_register &&
          !it.voided_on_register &&
          !it.paid_partial &&
          !it.corrected_deleted,
      ),
    );
  }, [tbl]);

  // Router κλήσης από τα δύο confirm dialogs. Αν το τραπέζι έχει σταλεί
  // στην ταμειακή → άνοιγμα force dialog (προειδοποίηση + κωδικός). Αλλιώς
  // απευθείας διαγραφή (η ταμειακή δεν γνωρίζει το τραπέζι).
  const requestDeleteTable = () => {
    setDeleteTableConfirm(false);
    setDeleteTableLastItem(false);
    if (tableHasFiscal) {
      setDeleteForce({ open: true, code: "" });
    } else {
      runDeleteTable();
    }
  };

  const runDeleteTable = async (adminCode) => {
    if (!tbl) return;
    setBusy(true);
    try {
      await http.post(
        `/tables/${tbl.id}/delete-table`,
        adminCode ? { admin_code: adminCode } : {},
      );
      // Clear any local correction state so the screen doesn't try to
      // re-apply a deleted item.
      setCorrections({});
      setSelectedSubmittedKey(null);
      setSelectedItemIdx(null);
      // Wipe table from local state and return to the grid.
      updateTable(tbl.id, {
        open_items: [],
        submitted_batches: [],
        session_opened_at: null,
      });
      toast.success(`Διαγραφή τραπεζιού ${tbl.name} ✓`);
      markRecentlyPaid(tbl.id, 5000);
      setDeleteTableConfirm(false);
      setDeleteTableLastItem(false);
      setDeleteForce({ open: false, code: "" });
      setSelectedTableId(null);
    } catch (e) {
      const status = e.response?.status;
      if (status === 428) {
        // Backend ζητά κωδικό διαχειριστή — άνοιξε το force dialog.
        setDeleteForce({ open: true, code: "" });
        return;
      }
      toast.error(e.response?.data?.detail || "Σφάλμα διαγραφής τραπεζιού");
    } finally {
      setBusy(false);
    }
  };

  // Total amount of currently picked items — drives the payment dialog.
  const partialPayTotal = (() => {
    let s = 0;
    for (const [key, qty] of partialPayQty.entries()) {
      const [bn, ii] = key.split(":").map(Number);
      const b = (tbl?.submitted_batches || []).find((x) => x.batch_no === bn);
      const it = b?.items?.[ii];
      if (!it) continue;
      const unit = Number(it.price || 0) + Number(it.modifier_total || 0);
      s += Number(qty) * unit;
    }
    return Math.round(s * 100) / 100;
  })();

  const confirmPartialPick = () => {
    if (partialPayQty.size === 0) {
      toast.error("Επιλέξτε τουλάχιστον ένα είδος");
      return;
    }
    // Check for unsent items — if any, warn the operator and let them
    // choose «send to ECR» (full payment options) vs «cash only» (no fiscal).
    let hasUnsent = false;
    for (const key of partialPayQty.keys()) {
      const [bn, ii] = key.split(":").map(Number);
      const b = (tbl?.submitted_batches || []).find((x) => x.batch_no === bn);
      const it = b?.items?.[ii];
      if (it && !it.sent_to_register) {
        hasUnsent = true;
        break;
      }
    }
    if (hasUnsent) {
      setPartialPayStage("warn");
    } else {
      setPartialPayCashOnly(false);
      setPartialPayStage("pay");
    }
  };

  const submitPartialPayment = async () => {
    if (!tbl) return;
    const selections = [];
    for (const [key, qty] of partialPayQty.entries()) {
      if (!qty || qty <= 0) continue;
      const [bn, ii] = key.split(":").map(Number);
      selections.push({ batch_no: bn, item_idx: ii, qty });
    }
    if (selections.length === 0) return;

    // Normalise the split rows the way POSRetail does for send-receipt:
    // empty amount → null (server auto-fills it with «remainder»). Then
    // for the client-side total check, treat null/empty as "the rest"
    // so a single-row pay with no typed amount still passes validation.
    const normalisedPayments = (partialPaySplit || [])
      .filter((p) => p && p.payment_id != null)
      .map((p) => {
        const amt = String(p.amount ?? "").trim().replace(",", ".");
        return {
          payment_id: Number(p.payment_id),
          amount: amt && Number.isFinite(Number(amt)) ? Number(amt) : null,
        };
      });
    if (normalisedPayments.length === 0) {
      toast.error("Επίλεξε τρόπο πληρωμής");
      return;
    }
    const fixed = normalisedPayments.reduce(
      (s, p) => s + (p.amount == null ? 0 : p.amount),
      0,
    );
    const nullCount = normalisedPayments.filter((p) => p.amount == null).length;
    if (nullCount === 0 && Math.abs(fixed - partialPayTotal) > 0.01) {
      toast.error(
        `Το ποσό δεν ταιριάζει: ${fixed.toFixed(2)} αντί για ${partialPayTotal.toFixed(2)}`,
      );
      return;
    }
    if (nullCount > 0 && fixed > partialPayTotal + 0.01) {
      toast.error("Το άθροισμα ξεπερνά το σύνολο");
      return;
    }
    setBusy(true);
    try {
      await http.post(`/tables/${tbl.id}/partial-payment`, {
        selections,
        payments: normalisedPayments,
        comment: partialPayComment || null,
        send_unsent_to_register: !partialPayCashOnly,
      });
      toastFiscalOk();
      // Reset dialog state and return to the tables grid — same UX as
      // the regular «Απόδειξη» close. Operator should never be left
      // looking at an order panel for a (possibly now-empty) table.
      setPartialPayOpen(false);
      setPartialPayStage("pick");
      setPartialPayQty(new Map());
      setPartialPaySplit([]);
      setPartialPayComment("");
      setPartialPayCashOnly(false);
      await loadAll();
      setSelectedTableId(null);
    } catch {
      toastFiscalFail();
    } finally {
      setBusy(false);
    }
  };

  const submitOrder = () => {
    if (!tbl) return;
    const hasOpen = (tbl.open_items || []).length > 0;
    const hasUnsent = allUnsentSelections(tbl).length > 0;
    const hasCorr = correctionsCount > 0;
    if (!hasOpen && !hasUnsent && !hasCorr) {
      toast.error("Δεν υπάρχει τίποτα προς καταχώρηση ή αποστολή");
      return;
    }
    // Bug fix: "Last item void → auto-delete table" — if applying the
    // pending deletes would empty the table (no remaining billable lines
    // + no open_items), warn the operator that this will trigger the full
    // ΔΙΑΓΡΑΦΗ ΤΡΑΠΕΖΙΟΥ sequence (void all + 2 cancellation receipts +
    // wipe) instead of leaving a ghost-empty open table.
    if (wouldEmptyTable) {
      setDeleteTableLastItem(true);
      return;
    }
    setSubmitChoiceOpen(true);
  };

  // Choice handlers — every variant now starts by flushing pending
  // corrections so the operator sees a single «Καταχώρηση» action that
  // ships diorthoseis + new items together (per user spec).
  const handleSimpleSubmit = async () => {
    setBusy(true);
    try {
      const refreshedFromCorr = await _flushCorrections();
      // After flushing, open_items may include fiscal replacement lines.
      const stateAfter = refreshedFromCorr || tbl;
      if ((stateAfter.open_items || []).length > 0) {
        await persistAsBatch();
      }
      // Bug fix: if there are now pending fiscal voids (corrections that
      // marked sent_to_register items as corrected_deleted), we MUST also
      // flush them to the register on a "Simple Submit" — otherwise the
      // physical cash register retains items the operator voided.
      // Re-fetch fresh table state from local store after the operations
      // above so we can detect any pending voids.
      const fresh = stateAfter;
      const pendingVoids = (fresh.submitted_batches || []).some((b) =>
        (b.items || []).some(
          (it) =>
            it.corrected_deleted &&
            it.sent_to_register &&
            typeof it.fiscal_line_no === "number" &&
            !it.voided_on_register,
        ),
      );
      if (pendingVoids) {
        await sendToRegister([], fresh);
      }
      toast.success(`Καταχωρήθηκε στο ${tbl.name}`);
      setSubmitChoiceOpen(false);
      markRecentlyUsed(tbl.id);
      setSelectedTableId(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    } finally {
      setBusy(false);
    }
  };

  const handleFullSendToRegister = async () => {
    setBusy(true);
    try {
      // Capture the fresh state returned by /apply-corrections so we
      // don't fall back to the stale `tbl` closure when there are no
      // open_items to persist (user bug: marking submitted items as
      // deleted without adding new items → submit said "no items").
      const corrFresh = await _flushCorrections();
      const refreshed = await persistAsBatch();
      const baseTable = refreshed || corrFresh || tbl;
      const selections = allUnsentSelections(baseTable);
      // Detect pending fiscal voids (Phase 3) even when there are no
      // selections — the user might be sending only corrections.
      const hasVoids = (baseTable.submitted_batches || []).some((b) =>
        (b.items || []).some(
          (it) => it.corrected_deleted && it.sent_to_register && it.fiscal_line_no && !it.voided_on_register,
        ),
      );
      if (selections.length === 0 && !hasVoids) {
        // Feb 2026 fix: αν πυροδοτήθηκε μόνο kitchen-only correction
        // (π.χ. αλλαγή σχολίου σε παγωτό ΦΥΣΤΙΚΙ → ΚΡΕΜΑ), τότε το
        // ``_flushCorrections`` ήδη έστειλε το ΔΙΟΡΘΩΣΗ ticket στην
        // κουζίνα. Δεν υπάρχει κάτι άλλο να σταλεί στην ταμειακή —
        // κλείνουμε τη ροή σιωπηλά αντί να πετάξουμε error.
        if (corrFresh) {
          setSubmitChoiceOpen(false);
          markRecentlyUsed(tbl.id);
          setSelectedTableId(null);
          return;
        }
        toast.error("Δεν υπάρχουν είδη προς αποστολή");
        return;
      }
      await sendToRegister(selections, baseTable);
      setSubmitChoiceOpen(false);
      markRecentlyUsed(tbl.id);
      setSelectedTableId(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    } finally {
      setBusy(false);
    }
  };

  const handlePartialSendStart = async () => {
    setBusy(true);
    try {
      // Capture the fresh state — after applying corrections the local
      // ``tbl`` closure is still stale (Redux is updated async).
      const corrFresh = await _flushCorrections();
      // ⚠️ ΣΗΜΑΝΤΙΚΟ — ΜΗΝ περσιστάρεις τα open_items εδώ! Το παλιό
      // implementation καλούσε `persistAsBatch()` ώστε να εμφανιστούν τα
      // νέα είδη στον picker, με αποτέλεσμα κάθε φορά που ο χρήστης
      // ακύρωνε το dialog να έχουν ΗΔΗ σταλεί χαρτάκια στην κουζίνα
      // (κατάχρηση όχι αναστρέψιμη — physical paper πάνω στους
      // εκτυπωτές). Πλέον το persist γίνεται μόνο όταν επιβεβαιωθεί
      // το partial submit στο `handlePartialConfirm` — βλ. εκεί τη
      // λογική virtual-batch.
      const state = corrFresh || tbl;
      const realUnsent = allUnsentSelections(state).length;
      const newOpen = (state.open_items || []).length;
      // Pending fiscal voids count toward "things to send" — without
      // this guard the operator can't send a pure correction-cancel.
      const hasVoids = (state.submitted_batches || []).some((b) =>
        (b.items || []).some(
          (it) => it.corrected_deleted && it.sent_to_register && it.fiscal_line_no && !it.voided_on_register,
        ),
      );
      if (realUnsent + newOpen === 0 && !hasVoids) {
        // Feb 2026 fix — same as handleFullSendToRegister: kitchen-only
        // correction ήδη εφαρμόστηκε. Κλείνουμε σιωπηλά.
        if (corrFresh) {
          setSubmitChoiceOpen(false);
          markRecentlyUsed(tbl.id);
          setSelectedTableId(null);
          return;
        }
        toast.error("Δεν υπάρχουν είδη προς αποστολή");
        return;
      }
      // Pre-select nothing — user picks manually.
      setPartialQty(new Map());
      setSubmitChoiceOpen(false);
      setPartialOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    } finally {
      setBusy(false);
    }
  };

  const handlePartialConfirm = async () => {
    if (!tbl) return;
    // Split selections into "real" (already-persisted batches, key
    // "<batch_no>:<idx>") and "virtual" (open_items not yet persisted,
    // key "new:<idx>"). Virtual items only exist when the operator
    // jumped straight from cart → Μερική Καταχώρηση without persisting
    // first; per spec, we postpone the kitchen-print + persist until
    // the user actually CONFIRMS — cancelling the dialog must leave
    // the cart untouched (this fixes the «πάτησα ακύρωση και η
    // παραγγελία πέρασε» bug).
    const realSelections = [];
    const virtualIdxs = []; // indices into tbl.open_items
    const virtualQtyByIdx = new Map();
    for (const [key, qty] of partialQty.entries()) {
      if (!qty || qty <= 0) continue;
      if (key.startsWith("new:")) {
        const i = Number(key.slice(4));
        virtualIdxs.push(i);
        virtualQtyByIdx.set(i, qty);
      } else {
        const [bn, ii] = key.split(":").map(Number);
        realSelections.push({ batch_no: bn, item_idx: ii, qty });
      }
    }
    if (realSelections.length === 0 && virtualIdxs.length === 0) {
      // Fallback (Feb 2026 iter88): αν ο χειριστής άνοιξε τη «Μερική
      // Αποστολή» χωρίς να διαλέξει τίποτα, θεωρούμε ότι θέλει «Απλή
      // Καταχώρηση» — persist ό,τι υπάρχει στα open_items χωρίς να
      // στείλουμε τίποτα στην ταμειακή. Το παλιό behaviour έβγαζε
      // toast «Επίλεξε τουλάχιστον ένα τμχ» και ακύρωνε.
      setBusy(true);
      try {
        if ((tbl.open_items || []).length > 0) {
          await persistAsBatch();
        }
        setPartialOpen(false);
        setPartialQty(new Map());
        markRecentlyUsed(tbl.id);
        setSelectedTableId(null);
        toast.success("Αποθηκεύτηκε χωρίς αποστολή στην ταμειακή");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Σφάλμα");
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      // Persist on confirm — only NOW we send to kitchen. If the user
      // selected only PART of the open_items, ALL open_items still go
      // to kitchen (per existing batch semantics) but only the picked
      // ones go to the fiscal register. Future iteration could split
      // open_items into "this-batch / next-batch" before persisting.
      let baseTable = tbl;
      let newBatchNo = null;
      if (virtualIdxs.length > 0) {
        const refreshed = await persistAsBatch();
        if (refreshed) {
          baseTable = refreshed;
          // The new batch is the LAST one in the list (highest batch_no).
          const newest = (refreshed.submitted_batches || []).slice(-1)[0];
          newBatchNo = newest?.batch_no ?? null;
        }
      }
      const selections = [...realSelections];
      if (newBatchNo != null) {
        // open_items[i] becomes new_batch.items[i] preserving order →
        // virtual idx == real item_idx in the freshly-created batch.
        for (const i of virtualIdxs) {
          selections.push({ batch_no: newBatchNo, item_idx: i, qty: virtualQtyByIdx.get(i) });
        }
      }
      await sendToRegister(selections, baseTable);
      setPartialOpen(false);
      setPartialQty(new Map());
      markRecentlyUsed(tbl.id);
      setSelectedTableId(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    } finally {
      setBusy(false);
    }
  };

  const closeTable = async () => {
    if (!tbl) return;
    setBusy(true);
    try {
      const r = await http.post("/ftext/restaurant-close", {
        table_nr: tbl.device_number,
        table_name: tbl.name,
        payments: closePayments.length
          ? closePayments.map((p) => ({
              payment_id: Number(p.payment_id),
              amount: p.amount ? Number(p.amount) : null,
            }))
          : [],
      });
      if (r.data.ok) {
        toastFiscalOk();
        // Wipe the persistent session so the next time the table opens it's empty
        try {
          const t = await http.post(`/tables/${tbl.id}/clear-session`);
          updateTable(tbl.id, {
            open_items: t.data.open_items,
            submitted_batches: t.data.submitted_batches,
            session_opened_at: t.data.session_opened_at,
          });
        } catch {
          updateTable(tbl.id, { open_items: [], submitted_batches: [], session_opened_at: null });
        }
        setCloseOpen(false);
        setClosePayments([]);
        // Επιστροφή στο grid τραπεζιών μετά από επιτυχημένη εξόφληση,
        // ώστε ο σερβιτόρος να επιλέξει το επόμενο τραπέζι αμέσως.
        setSelectedItemIdx(null);
        setSelectedSubmittedKey(null);
        setCorrections({});
        // Flash «✓ Πληρωμένο» pulse στο grid για 5s πριν το τραπέζι γίνει
        // ξανά πράσινο/ΕΛΕΥΘΕΡΟ — visual cue για τον σερβιτόρο.
        markRecentlyPaid(tbl.id, 5000);
        setSelectedTableId(null);
      } else {
        toastFiscalFail();
      }
    } catch {
      toastFiscalFail();
    } finally {
      setBusy(false);
    }
  };

  // ----- Table transfer (μεταφορά) -----
  // Step 1: user presses "Μεταφορά" on the open table → we go back to the grid
  //         and the next clicked table becomes the destination.
  // Step 2: on destination click, validate the target is empty. If not, show
  //         "ΤΡΑΠΕΖΙ ΣΕ ΧΡΗΣΗ" and return to source table without doing anything.
  const startTransfer = () => {
    if (!tbl) return;
    setTransferSourceId(tbl.id);
    setTransferMode(true);
    setSelectedTableId(null);
  };

  const cancelTransfer = () => {
    const src = transferSourceId;
    setTransferMode(false);
    setTransferSourceId(null);
    if (src) setSelectedTableId(src);
  };

  // Merge-confirmation dialog state. Populated by the backend's 409
  // ``DESTINATION_OCCUPIED`` response so the UI can show the operator a
  // structured prompt («Το τραπέζι X έχει N είδη — επιβεβαίωση;»). On
  // confirm we retry the transfer with ``?force=true``; on cancel we
  // return to the source table (the user's last-known good state).
  const [transferConflict, setTransferConflict] = useState(null);

  const _doTransfer = async (src, dst, force = false) => {
    setBusy(true);
    try {
      const qs = force ? "?force=true" : "";
      await http.post(`/tables/${src.id}/transfer-to/${dst.id}${qs}`);
      await loadAll();
      toast.success(
        force
          ? `Συγχώνευση ${src.name} → ${dst.name} ✓`
          : `Μεταφορά ${src.name} → ${dst.name} ✓`,
      );
      setTransferMode(false);
      setTransferSourceId(null);
      setTransferConflict(null);
      setSelectedTableId(dst.id);
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend serialises {code, dst_name, items, message} on 409 when
      // the destination already has items and ``force`` wasn't supplied.
      if (
        e.response?.status === 409 &&
        typeof detail === "object" &&
        detail?.code === "DESTINATION_OCCUPIED"
      ) {
        setTransferConflict({ src, dst, info: detail });
        return false;
      }
      toast.error(
        typeof detail === "string"
          ? detail
          : detail?.message || "Σφάλμα μεταφοράς",
      );
      // Other errors → bail and return to source table.
      const srcId = transferSourceId;
      setTransferMode(false);
      setTransferSourceId(null);
      setSelectedTableId(srcId);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const executeTransfer = async (dst) => {
    const src = tables.find((t) => t.id === transferSourceId);
    if (!src || !dst) return;
    if (dst.id === src.id) {
      // tapping the source itself → just cancel and return to the table view
      cancelTransfer();
      return;
    }
    // First attempt — without ``force``. Backend will pick the right
    // protocol (REST_TABLE_MOVE for empty destinations, VOID+REPLAY for
    // occupied ones AFTER the operator confirms the merge).
    await _doTransfer(src, dst, false);
  };

  const confirmTransferMerge = async () => {
    const t = transferConflict;
    if (!t) return;
    await _doTransfer(t.src, t.dst, true);
  };

  const cancelTransferMerge = () => {
    // Operator declined the merge — bail out cleanly and return to the
    // source table so they can revisit / pick another destination.
    const srcId = transferConflict?.src?.id ?? transferSourceId;
    setTransferConflict(null);
    setTransferMode(false);
    setTransferSourceId(null);
    if (srcId) setSelectedTableId(srcId);
  };

  // Ακύρωση & Επιστροφή — διαγράφει τα νέα είδη και γυρνά πίσω στα τραπέζια.
  // Τα ήδη καταχωρημένα batches παραμένουν αμετάβλητα.
  const cancelAndReturn = () => {
    if (!tbl) return;
    if ((tbl.open_items || []).length === 0) {
      // No unsaved items → return immediately without confirmation
      setSelectedItemIdx(null);
      setSelectedTableId(null);
      return;
    }
    setCancelConfirmOpen(true);
  };

  const confirmCancelAndReturn = async () => {
    if (!tbl) return;
    setBusy(true);
    try {
      await persistOpenItems(tbl.id, []);
      updateTable(tbl.id, { open_items: [] });
      setSelectedItemIdx(null);
      setCancelConfirmOpen(false);
      setSelectedTableId(null);
    } finally {
      setBusy(false);
    }
  };

  const printOpenTables = async () => {
    try {
      const r = await http.post("/ftext/rest-print-report");
      if (r.data.ok) toast.success("Στάλθηκε αναφορά ανοιχτών τραπεζιών");
      else toast.error(r.data.result?.result_description || "Σφάλμα");
    } catch (e) {
      toast.error("Σφάλμα");
    }
  };

  // ----- «Έλεγχος Ανοιχτών Τραπεζιών» floating panel -----
  // Two routes: «ΟΛΑ» → printOpenTables (REST_PRINT_REPORT, same backend
  // command as before), numpad → REST_TABLE_STATUS|<nr> for a single
  // table. We keep the panel open after «ΟΛΑ» so the operator can also
  // check a specific table in one session; closing is explicit via the
  // X button or «Άκυρο».
  // SECRET — typing «9999» + OK triggers the bulk-flush warning dialog,
  // which on confirm flushes ALL unsent items in every table to the cash
  // register WITHOUT printing kitchen tickets.
  const [openTablesCheck, setOpenTablesCheck] = useState(false);
  const [bulkFlushWarn, setBulkFlushWarn] = useState(false);
  const checkSingleTable = async (nr) => {
    try {
      // Feb 2026: πλέον στέλνουμε REST_PRINT_REPORT|<nr> ώστε να τυπωθεί
      // χαρτί με τον έλεγχο συγκεκριμένου τραπεζιού (όχι απλό status
      // query που δεν έβγαζε τίποτα στον εκτυπωτή).
      const r = await http.post(`/ftext/rest-print-table/${nr}`);
      if (r.data.ok) {
        if (r.data.fallback) {
          toast.info(
            r.data.fallback_reason ||
            `Έλεγχος Τραπεζιού #${nr}: το firmware δεν δέχτηκε filter, τυπώθηκε ολόκληρη η αναφορά.`,
            { duration: 6000 },
          );
        } else {
          toast.success(`Στάλθηκε έλεγχος τραπεζιού #${nr}`);
        }
      } else {
        toast.error(r.data.result?.result_description || `Σφάλμα ελέγχου τραπεζιού #${nr}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα");
    } finally {
      setOpenTablesCheck(false);
    }
  };
  const handleAllOpenTables = async () => {
    await printOpenTables();
    setOpenTablesCheck(false);
  };
  const runBulkFlush = async () => {
    setBusy(true);
    try {
      const r = await http.post("/tables/bulk-flush-to-register");
      const d = r.data || {};
      const errs = (d.errors || []).length;
      if (errs > 0) {
        toast.error(
          `Έλεγχος: ${d.tables_processed} τραπέζια / ${d.items_flushed} είδη · ${errs} σφάλματα`,
        );
      } else {
        toast.success(
          `Έλεγχος Τραπεζιών ✓ · ${d.tables_processed} τραπέζια · ${d.items_flushed} είδη → ταμειακή`,
        );
      }
      // Force re-fetch of all tables so the UI reflects the new
      // sent_to_register state immediately.
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Σφάλμα κρυφής λειτουργίας");
    } finally {
      setBusy(false);
      setBulkFlushWarn(false);
      setOpenTablesCheck(false);
    }
  };

  // ----- Tables grid (overview) -----
  // Returns one of: "free" | "ordering" | "mixed" | "pending"
  //   free      → green   (no items)
  //   ordering  → orange  (όλα τα είδη unsent στην ταμειακή)
  //   mixed     → red     (μερικά sent, μερικά unsent)
  //   pending   → yellow  (όλα sent, εκκρεμεί πληρωμή)
  // The «✓ ΠΛΗΡΩΜΕΝΟ» pulse is a transient view-state; we treat it as
  // «free» for filtering purposes since the table is technically closed.
  const getTableStatus = (t) => {
    const open = (t.open_items || []).length;
    const sent = (t.submitted_batches || []).flatMap((b) => b.items || []);
    const sentCount = sent.filter((it) => it.sent_to_register).length;
    const unsentCount = sent.length - sentCount + open;
    const totalCount = sent.length + open;
    if (totalCount === 0) return "free";
    if (sentCount === 0) return "ordering";
    if (unsentCount === 0) return "pending";
    return "mixed";
  };

  if (!selectedTableId) {
    const cols = settings?.tables_cols ?? 6;
    const rows = settings?.tables_rows ?? 4;
    const transferSource = transferMode
      ? tables.find((t) => t.id === transferSourceId)
      : null;
    const sectionById = new Map(sections.map((s) => [s.id, s]));
    // Apply the section radio (single) + status multi-select to the
    // table list before rendering. Empty filters short-circuit to the
    // full list — the default («ΟΛΑ») screen on every entry per spec.
    // EXCEPTION: in transfer mode we ignore filters entirely so the
    // operator can pick any destination, even if it would normally be
    // hidden by the active section/status combination.
    const visibleTables = transferMode
      ? tables
      : tables.filter((t) => {
          if (activeSectionId && t.section_id !== activeSectionId) return false;
          if (activeStatuses.length > 0) {
            const st = getTableStatus(t);
            if (!activeStatuses.includes(st)) return false;
          }
          return true;
        });
    if (servingOpen) {
      return <ServingScreen onClose={() => setServingOpen(false)} />;
    }
    return (
      <div className="h-full flex flex-col">
        {transferMode ? (
          <>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-grid">
              <div>
                <h2 className="font-heading text-2xl font-bold">Μεταφορά Τραπεζιού</h2>
                <div className="text-xs text-slate-500 mt-1">
                  {`Επιλέξτε κενό τραπέζι προορισμού για μεταφορά από: ${transferSource?.name ?? ""}`}
                </div>
              </div>
              <Button
                variant="outline"
                onClick={cancelTransfer}
                data-testid="cancel-transfer-btn"
              >
                <XIcon size={16} className="mr-1.5" />
                Άκυρο
              </Button>
            </div>
            <div
              className="px-6 py-2 bg-amber-100 border-b border-amber-300 text-amber-900 text-sm font-semibold flex items-center gap-2"
              data-testid="transfer-banner"
            >
              <Move size={16} />
              Λειτουργία μεταφοράς — πατήστε <span className="underline">οποιοδήποτε</span> τραπέζι (κενά: άμεσα · κατειλημμένα: με επιβεβαίωση συγχώνευσης).
            </div>
          </>
        ) : (
          <TableGridFilters
            sections={sections}
            tables={tables}
            activeSectionId={activeSectionId}
            setActiveSectionId={setActiveSectionId}
            activeStatuses={activeStatuses}
            setActiveStatuses={setActiveStatuses}
            getTableStatus={getTableStatus}
            onPrintOpenTables={() => setOpenTablesCheck(true)}
            onOpenServing={settings?.modules_serving !== false ? () => setServingOpen(true) : undefined}
          />
        )}
        <div className="flex-1 overflow-y-auto scroll-thin p-4 bg-slate-50">
          <div
            className="grid gap-4 w-full"
            style={{
              gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
              gridAutoRows: `minmax(110px, calc((100vh - 220px) / ${rows}))`,
            }}
            data-testid="tables-grid"
          >
            {visibleTables.map((t) => {
              const allItems = [
                ...(t.open_items || []).map(() => ({ sent: false })),
                ...(t.submitted_batches || []).flatMap((b) =>
                  (b.items || []).map((it) => ({ sent: !!it.sent_to_register })),
                ),
              ];
              const totalItems = allItems.length;
              const sentCount = allItems.filter((x) => x.sent).length;
              const unsentCount = totalItems - sentCount;
              const isOpen = totalItems > 0;
              // Four states:
              //   ΕΛΕΥΘΕΡΟ          → green (no items)
              //   ΠΟΡΤΟΚΑΛΙ          → all items unsent (κανένα δεν έχει περάσει στην ταμειακή)
              //   ΚΟΚΚΙΝΟ            → mixed (μερικά sent, μερικά unsent)
              //   ΚΙΤΡΙΝΟ (ΣΕ ΑΝΑΜΟΝΗ) → όλα sent (πληρωμή pending)
              const allUnsent = isOpen && sentCount === 0;
              const mixed = isOpen && sentCount > 0 && unsentCount > 0;
              const allSent = isOpen && unsentCount === 0;
              // Feb 2026 — compute totals from items directly (same as mobile)
              // so modifier_total / discount_percent are always reflected. The
              // legacy ``b.total`` field is stamped once by the backend and
              // doesn't get recomputed after corrections or extras — trusting
              // it made a Burger 8,00 + Πατάτες 2,00 line show as 8,00.
              const subTotal = (t.submitted_batches || []).reduce((s, b) => {
                for (const it of b.items || []) {
                  if (it.corrected_deleted) continue;
                  if (it.paid_partial) continue;
                  const unit = Number(it.price || 0) + Number(it.modifier_total || 0);
                  const gross = Number(it.qty || 0) * unit;
                  s += it.discount_percent
                    ? gross * (1 - Number(it.discount_percent) / 100)
                    : gross;
                }
                return s;
              }, 0);
              const openTotal = (t.open_items || []).reduce((s, it) => {
                const gross =
                  Number(it.qty || 0) *
                  (Number(it.price || 0) + Number(it.modifier_total || 0));
                return s + (it.discount_percent ? gross * (1 - Number(it.discount_percent) / 100) : gross);
              }, 0);
              const batchCount = (t.submitted_batches || []).length;
              const isJustPaid = recentlyPaid.has(t.id);
              const bg = isJustPaid
                ? "#10B981" // ΠΛΗΡΩΜΕΝΟ — emerald (vivid green)
                : allSent
                ? "#F59E0B" // ΚΙΤΡΙΝΟ (amber)
                : mixed
                ? "#EF4444" // ΚΟΚΚΙΝΟ
                : allUnsent
                ? "#F97316" // ΠΟΡΤΟΚΑΛΙ (orange)
                : (t.color || "#10B981");
              const borderColor = isJustPaid
                ? "#047857"
                : allSent
                ? "#B45309"
                : mixed
                ? "#B91C1C"
                : allUnsent
                ? "#C2410C"
                : (t.color || "#059669");
              const ringClass = isJustPaid
                ? "ring-4 ring-emerald-300 animate-pulse"
                : mixed
                ? "ring-4 ring-red-300"
                : allUnsent
                ? "ring-4 ring-orange-300"
                : allSent
                ? "ring-4 ring-amber-300"
                : "";
              const statusLabel = isJustPaid
                ? "✓ ΠΛΗΡΩΜΕΝΟ"
                : allSent
                ? "ΣΕ ΑΝΑΜΟΝΗ"
                : mixed
                ? "ΜΕΡΙΚΩΣ ΣΤΑΛΘΗΚΕ"
                : allUnsent
                ? "ΣΕ ΠΑΡΑΓΓΕΛΙΑ"
                : "ΕΛΕΥΘΕΡΟ";
              const isTransferSource = transferMode && t.id === transferSourceId;
              // Section accent — a 6px outer outline using the section
              // colour so the operator can tell from across the room
              // which zone a table belongs to without touching the
              // existing status colour palette (πράσινο/κίτρινο/
              // πορτοκαλί/κόκκινο stays untouched per requirements).
              const section = t.section_id ? sectionById.get(t.section_id) : null;
              const sectionAccent = section
                ? { boxShadow: `0 0 0 6px ${section.color}` }
                : {};
              // Elapsed minutes since the first batch was registered. The
              // backend stamps ``session_opened_at`` (UTC ISO) when the
              // first /submit-batch runs, exactly matching the user's
              // requirement: ο χρόνος ξεκινάει από την πρώτη καταχώρηση.
              let elapsedLabel = "";
              if (isOpen && t.session_opened_at) {
                const startMs = Date.parse(t.session_opened_at);
                if (Number.isFinite(startMs)) {
                  const diffMin = Math.max(0, Math.floor((nowTick - startMs) / 60000));
                  if (diffMin < 60) {
                    elapsedLabel = `${diffMin}′`;
                  } else {
                    const h = Math.floor(diffMin / 60);
                    const m = diffMin % 60;
                    elapsedLabel = `${h}ω ${String(m).padStart(2, "0")}′`;
                  }
                }
              }
              return (
              <button
                key={t.id}
                onClick={() => (transferMode ? executeTransfer(t) : setSelectedTableId(t.id))}
                data-testid={`table-${t.position}`}
                disabled={busy || isTransferSource}
                className={`rounded-lg shadow-sm border-4 transition-all active:scale-[0.97] hover:brightness-95 font-heading flex flex-col items-center justify-center text-white p-2 relative ${ringClass} ${recentlyUsedTableId === t.id ? "ring-4 ring-yellow-300 animate-pulse" : ""} ${isTransferSource ? "opacity-50 cursor-not-allowed" : ""}`}
                style={{ background: bg, borderColor, ...sectionAccent }}
              >
                <div className="text-xl font-bold leading-tight text-center line-clamp-2">
                  {t.name}
                </div>
                <div className="text-[10px] opacity-90 mt-1 tracking-wider font-bold">
                  {statusLabel}
                </div>
                {isOpen && (
                  <div className="mt-1 flex flex-col items-center text-[11px] font-mono">
                    {batchCount > 0 && (
                      <span className="px-1.5 py-0.5 rounded bg-white/20">
                        {batchCount} παραγγ.
                      </span>
                    )}
                    <span className="font-bold text-sm font-mono mt-0.5">
                      {fmtEUR(subTotal + openTotal)}
                    </span>
                  </div>
                )}
                {elapsedLabel && (
                  <div
                    className="absolute top-1 left-2 text-[10px] font-mono font-bold bg-black/25 rounded px-1 py-0.5"
                    data-testid={`table-${t.position}-elapsed`}
                    title="Χρόνος ανοιχτού τραπεζιού"
                  >
                    ⏱ {elapsedLabel}
                  </div>
                )}
                <div className="absolute bottom-1 right-2 text-[10px] opacity-80 font-mono">
                  #{t.device_number}
                </div>
              </button>
              );
            })}
            {tables.length === 0 && (
              <div className="col-span-full text-center text-slate-400 py-12">
                Δεν υπάρχουν τραπέζια. Δημιούργησε από Ρυθμίσεις → Τραπέζια.
              </div>
            )}
          </div>
        </div>

        {/* Transfer-merge confirmation — fires when the chosen destination
            already has items on it. Operator can confirm (server runs the
            VOID+CLOSE+REPLAY merge with ``force=true``) or cancel (we bail
            back to the source table). */}
        <ConfirmDialog
          open={!!transferConflict}
          onOpenChange={(open) => {
            if (!open) cancelTransferMerge();
          }}
          title="Επιβεβαίωση συγχώνευσης τραπεζιών"
          description={transferConflict?.info?.message}
          subject={`${transferConflict?.src?.name ?? ""} → ${transferConflict?.dst?.name ?? ""}`}
          rows={[
            {
              label: "Είδη στο τραπέζι προορισμού",
              value: transferConflict?.info?.items ?? 0,
              accent: "amber",
            },
            {
              label: "Είδη προς μεταφορά",
              value:
                ((transferConflict?.src?.open_items || []).length) +
                ((transferConflict?.src?.submitted_batches || []).reduce(
                  (n, b) =>
                    n + (b.items || []).filter((it) => !it.corrected_deleted).length,
                  0,
                )),
            },
          ]}
          confirmLabel="Ναι, συγχώνευση"
          cancelLabel="Άκυρο"
          onConfirm={confirmTransferMerge}
          busy={busy}
          testidPrefix="transfer-merge"
        />
        {/* ---- Floating «Έλεγχος Ανοιχτών Τραπεζιών» panel (grid view) ---- */}
        <OpenTablesCheckPanel
          open={openTablesCheck}
          onClose={() => setOpenTablesCheck(false)}
          onPrintAll={handleAllOpenTables}
          onCheckOne={checkSingleTable}
          onSecretBulkFlush={() => setBulkFlushWarn(true)}
        />
        {/* ---- SECRET 9999 — bulk-flush warning ---- */}
        <ConfirmDialog
          open={bulkFlushWarn}
          onOpenChange={setBulkFlushWarn}
          title="Έλεγχος Όλων των Τραπεζιών"
          description="Όλα τα είδη που ΔΕΝ έχουν περαστεί στην ταμειακή θα σταλούν τώρα. Δεν θα εκτυπωθούν χαρτάκια στους βοηθητικούς εκτυπωτές (κουζίνα/μπαρ). Η λειτουργία είναι ΜΗ ΕΠΙΣΤΡΕΨΙΜΗ."
          confirmLabel="OK — Συνέχεια"
          cancelLabel="Επιστροφή"
          onConfirm={runBulkFlush}
          busy={busy}
          testidPrefix="bulk-flush-warn"
        />
      </div>
    );
  }

  // ----- Order view -----
  return (
    <div className="h-full flex">
      <section className="w-[36%] min-w-[380px] h-full flex flex-col border-r border-slate-200 bg-white">
        <div className="px-5 py-4 border-b border-slate-200 bg-grid">
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full"
              style={{ background: tbl?.color || "#10B981" }}
            />
            <h2 className="font-heading text-xl font-bold flex-1">{tbl?.name}</h2>
            <span className="text-xs text-slate-500 font-mono">#{tbl?.device_number}</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scroll-thin" data-testid="order-list">
          {/* Past submitted batches — collapsible to save vertical space */}
          {(tbl.submitted_batches || []).length > 0 && (
            <div className="bg-emerald-50/50 border-b-2 border-emerald-200">
              <button
                type="button"
                onClick={() => setBatchesExpanded((v) => !v)}
                data-testid="batches-toggle"
                className="w-full px-4 py-2 text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 hover:bg-emerald-200 transition-colors flex items-center justify-between gap-2"
              >
                <span className="flex items-center gap-1.5">
                  {(batchesExpanded || correctionsCount > 0 || !!selectedSubmittedKey) ? (
                    <ChevronDown size={14} />
                  ) : (
                    <ChevronRight size={14} />
                  )}
                  Καταχωρημένες Παραγγελίες ({(tbl.submitted_batches || []).length})
                  {correctionsCount > 0 && (
                    <span
                      data-testid="corrections-badge"
                      className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold"
                    >
                      <Pencil size={10} /> {correctionsCount} διορθ.
                    </span>
                  )}
                </span>
                <span className="font-mono text-emerald-700 text-[11px]">
                  Σύνολο {fmtEUR(totals.submitted)}
                </span>
              </button>
              {(batchesExpanded || correctionsCount > 0 || !!selectedSubmittedKey) && (tbl.submitted_batches || []).map((b, bi) => {
                const tmx = (b.items || []).reduce((s, it) => s + Number(it.qty || 0), 0);
                const tstr = b.timestamp
                  ? new Date(b.timestamp).toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" })
                  : "—";
                return (
                  <div key={bi} className="px-4 py-2 border-b border-emerald-100" data-testid={`batch-${b.batch_no}`}>
                    {/* Compact header — all metadata on a single row */}
                    <div className="flex items-center justify-between text-sm gap-2">
                      <div className="font-bold text-emerald-900 truncate flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            reprintBatch(b.batch_no);
                          }}
                          title={`Επανεκτύπωση παραγγελίας #${b.batch_no}`}
                          data-testid={`reprint-batch-${b.batch_no}`}
                          className="w-6 h-6 grid place-items-center rounded hover:bg-emerald-200 text-emerald-700 active:scale-90 transition-all"
                        >
                          <Printer size={14} />
                        </button>
                        <span>
                          #{b.batch_no} · {b.user_name || "—"}
                          <span className="font-normal text-slate-500"> · {tstr} · {tmx} τμχ</span>
                        </span>
                      </div>
                      <div className="font-mono text-emerald-700 font-bold shrink-0">
                        {fmtEUR(b.total || 0)}
                      </div>
                    </div>
                    <ul className="text-sm text-slate-800 mt-1.5 space-y-0">
                      {(b.items || []).map((origIt, i) => {
                        const eff = getEffectiveBatchItem(b.batch_no, i, origIt);
                        const it = eff.item;
                        // Persisted history flags (from /apply-corrections).
                        const committedDeleted = !!origIt.corrected_deleted;
                        const committedModified = !!origIt.corrected_modified && !committedDeleted;
                        const isReplacement = !!origIt.is_correction_replacement;
                        const isLocked = committedDeleted;
                        const isSelected = !isLocked && selectedSubmittedKey === eff.key;
                        const pendingDeleted = !committedDeleted && eff.deleted;
                        const pendingEdited = !committedDeleted && eff.corrected && !pendingDeleted;
                        const visuallyDeleted = committedDeleted || pendingDeleted;
                        const visuallyEdited = committedModified || pendingEdited;
                        const isFiscal = !!origIt.sent_to_register;
                        const isPaidPartial = !!origIt.paid_partial;
                        const unit = Number(it.price || 0) + Number(it.modifier_total || 0);
                        const gross = Number(it.qty || 0) * unit;
                        const disc = it.discount_percent ? (gross * Number(it.discount_percent)) / 100 : 0;
                        const net = Math.max(0, gross - disc);
                        const snap = origIt.original_snapshot || null;
                        const snapNet = snap
                          ? (() => {
                              const u = Number(snap.price || 0) + Number(snap.modifier_total || 0);
                              const g = (Number(snap.qty) || 0) * u;
                              return snap.discount_percent ? g * (1 - Number(snap.discount_percent) / 100) : g;
                            })()
                          : 0;
                        // Base styling: zebra, selection ring, edited/deleted overlays.
                        const baseBg = i % 2 === 0 ? "bg-white" : "bg-emerald-50/60";
                        const rowBg = isPaidPartial
                          ? "bg-yellow-100 border-l-4 border-yellow-500"
                          : committedDeleted
                          ? "bg-slate-100 border-l-4 border-slate-400 opacity-80"
                          : pendingDeleted
                          ? "bg-red-50 border-l-4 border-red-400"
                          : committedModified
                          ? "bg-emerald-50 border-l-4 border-emerald-500"
                          : pendingEdited
                          ? "bg-amber-50 border-l-4 border-amber-400"
                          : isSelected
                          ? "bg-blue-50 border-l-4 border-blue-400"
                          : `${baseBg} border-l-4 border-transparent`;
                        return (
                        <li
                          key={i}
                          data-testid={`submitted-item-${b.batch_no}-${i}`}
                          className={`rounded transition-colors ${rowBg}`}
                        >
                          <button
                            type="button"
                            onClick={() => !isLocked && toggleSubmittedSelection(eff.key)}
                            disabled={isLocked}
                            className={`w-full text-left px-2 py-1 flex justify-between items-start gap-1.5 ${isLocked ? "cursor-default" : ""}`}
                          >
                            <span className="flex-1 min-w-0 font-medium">
                              <span className="inline-flex items-center gap-1 mr-1">
                                {isFiscal ? (
                                  <Receipt size={13} className="text-blue-600" />
                                ) : (
                                  <ChefHat size={13} className="text-orange-600" />
                                )}
                                {isReplacement && (
                                  <span className="text-[10px] font-bold uppercase tracking-wider px-1 bg-indigo-100 text-indigo-700 rounded">νέο</span>
                                )}
                                {committedDeleted && (
                                  <span className="text-[10px] font-bold uppercase tracking-wider px-1 bg-slate-300 text-slate-700 rounded">διαγρ.</span>
                                )}
                                {committedModified && (
                                  <span className="text-[10px] font-bold uppercase tracking-wider px-1 bg-emerald-200 text-emerald-800 rounded inline-flex items-center gap-0.5"><Pencil size={9} /> διορθ.</span>
                                )}
                                {pendingEdited && <Pencil size={12} className="text-amber-700" />}
                                {pendingDeleted && <Trash2 size={12} className="text-red-600" />}
                                {isPaidPartial && (
                                  <span className="text-[10px] font-bold uppercase tracking-wider px-1 bg-yellow-400 text-yellow-900 rounded">πληρωμένο</span>
                                )}
                              </span>
                              <span className={(visuallyDeleted || isPaidPartial) ? "line-through text-slate-500" : ""}>
                                {it.description} × {it.qty}
                              </span>
                              {it.discount_percent ? (
                                <span className="ml-1 text-xs font-mono text-red-600">-{it.discount_percent}%</span>
                              ) : null}
                              {it.comment && (
                                <span className={`block text-xs italic mt-0.5 ${visuallyDeleted ? "line-through text-slate-400" : "text-blue-700"}`}>
                                  › {it.comment}
                                </span>
                              )}
                              {/* Audit row for committed modifications — show prior qty/price snapshot. */}
                              {committedModified && snap && (
                                <span className="block text-[10px] text-slate-400 line-through mt-0.5">
                                  ήταν: {snap.qty} × {Number(snap.price).toFixed(2)}€
                                </span>
                              )}
                            </span>
                            <span className="text-right shrink-0 font-mono tabular font-semibold">
                              {(pendingEdited || committedModified) && (
                                <span className="block text-[10px] text-slate-400 line-through leading-none">
                                  {fmtEUR(committedModified && snap ? snapNet
                                    : (Number(origIt.qty) || 0) *
                                        ((Number(origIt.price) || 0) + Number(origIt.modifier_total || 0)) *
                                        (origIt.discount_percent ? 1 - Number(origIt.discount_percent) / 100 : 1))}
                                </span>
                              )}
                              <span className={visuallyDeleted ? "line-through text-slate-400" : "text-slate-700"}>
                                {fmtEUR(committedDeleted ? snapNet : net)}
                              </span>
                            </span>
                          </button>
                          {/* Inline correction toolbar — appears beneath the
                              selected submitted item. Mirrors the open-item
                              CartRow controls but writes into ``corrections``
                              instead of mutating the batch. Locked rows
                              (committed deletions) skip this entirely. */}
                          {isSelected && !isLocked && (
                            <div
                              className="grid grid-cols-10 gap-1.5 px-2 pb-2 pt-1"
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`submitted-item-${b.batch_no}-${i}-toolbar`}
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  openNumpad({ kind: "qty", submittedKey: eff.key }, "ΠΟΣΟΤΗΤΑ", String(it.qty || ""))
                                }
                                disabled={pendingDeleted}
                                className="col-span-2 h-12 rounded-md border-2 border-slate-300 bg-white hover:bg-amber-50 active:scale-[0.97] transition-all flex flex-col items-center justify-center disabled:opacity-40 disabled:pointer-events-none"
                                data-testid={`submitted-item-${b.batch_no}-${i}-qty`}
                              >
                                <span className="text-[9px] tracking-wider text-slate-500 font-bold">ΠΟΣΟΤΗΤΑ</span>
                                <span className="text-xl font-bold font-mono tabular leading-none">{it.qty}</span>
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  openNumpad({ kind: "price", submittedKey: eff.key }, "ΤΙΜΗ €", String(it.price || ""))
                                }
                                disabled={pendingDeleted}
                                className="col-span-2 h-12 rounded-md border-2 border-slate-300 bg-white hover:bg-amber-50 active:scale-[0.97] transition-all flex flex-col items-center justify-center disabled:opacity-40 disabled:pointer-events-none"
                                data-testid={`submitted-item-${b.batch_no}-${i}-price`}
                              >
                                <span className="text-[9px] tracking-wider text-slate-500 font-bold">ΤΙΜΗ €</span>
                                <span className="text-xl font-bold font-mono tabular leading-none">{Number(it.price).toFixed(2)}</span>
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  openNumpad({ kind: "disc", submittedKey: eff.key }, "ΕΚΠΤΩΣΗ %", String(it.discount_percent || ""))
                                }
                                disabled={pendingDeleted}
                                className="col-span-2 h-12 rounded-md border-2 border-slate-300 bg-white hover:bg-amber-50 active:scale-[0.97] transition-all flex flex-col items-center justify-center disabled:opacity-40 disabled:pointer-events-none"
                                data-testid={`submitted-item-${b.batch_no}-${i}-disc`}
                              >
                                <span className="text-[9px] tracking-wider text-slate-500 font-bold">ΕΚΠΤΩΣΗ %</span>
                                <span className="text-xl font-bold font-mono tabular leading-none text-red-600">
                                  {it.discount_percent ? `-${it.discount_percent}` : "—"}
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={() => openCommentDialogForSubmitted(eff.key, it)}
                                disabled={pendingDeleted || !(origIt.allow_comment || (it.comments || []).length || (origIt.comment_group_ids || []).length)}
                                className="col-span-2 h-12 rounded-md border-2 border-blue-300 bg-white text-blue-600 hover:bg-blue-50 active:scale-[0.97] transition-all flex items-center justify-center disabled:opacity-40 disabled:pointer-events-none"
                                data-testid={`submitted-item-${b.batch_no}-${i}-comment-btn`}
                              >
                                <Pencil size={16} className="mr-1" />
                                <span className="text-xs font-bold">Σχόλιο</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleDeleteSubmitted(eff.key)}
                                className={`col-span-2 h-12 rounded-md border-2 active:scale-[0.97] transition-all flex items-center justify-center ${
                                  pendingDeleted
                                    ? "border-slate-400 bg-slate-100 text-slate-700 hover:bg-slate-200"
                                    : "border-red-300 bg-red-50 text-red-600 hover:bg-red-100"
                                }`}
                                data-testid={`submitted-item-${b.batch_no}-${i}-delete`}
                              >
                                {pendingDeleted ? (
                                  <><Undo2 size={16} className="mr-1" /><span className="text-xs font-bold">Επαναφορά</span></>
                                ) : (
                                  <><Trash2 size={16} className="mr-1" /><span className="text-xs font-bold">Διαγραφή</span></>
                                )}
                              </button>
                            </div>
                          )}
                        </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}

          {/* Open items area — new order being built */}
          <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-700 bg-amber-50 border-b border-amber-200">
            Νέα Είδη {(tbl.open_items || []).length > 0 ? `(${(tbl.open_items || []).length})` : "— πρόσθεσε από τα δεξιά"}
          </div>
          {(tbl.open_items || []).length === 0 && (tbl.submitted_batches || []).length === 0 && (
            <div className="h-32 grid place-items-center text-slate-400 text-sm">
              Πρόσθεσε είδη από τη δεξιά λίστα
            </div>
          )}
          {(tbl.open_items || []).map((it, i) => (
            <CartRow
              key={i}
              item={it}
              idx={i}
              selected={selectedItemIdx === i}
              onSelect={() => {
                setSelectedItemIdx(selectedItemIdx === i ? null : i);
                setSelectedSubmittedKey(null);
              }}
              onChange={(patch) => updateOpenItem(i, patch)}
              onRemove={() => removeOpenItem(i)}
              openNumpad={openNumpad}
              onCommentClick={openCommentDialog}
              testid={`order-row-${i}`}
            />
          ))}
        </div>

        {/* Search bar above total — same UX position as Retail */}
        <div className="px-5 pt-2 pb-1 border-t-2 border-slate-300 bg-white">
          <div className="relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Αναζήτηση: περιγραφή, κωδικός ή barcode…"
              data-testid="rest-search-input"
              className="w-full h-10 pl-10 pr-10 rounded-md border-2 border-slate-200 bg-white text-base focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                data-testid="rest-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md grid place-items-center text-slate-500 hover:bg-slate-100"
                aria-label="Καθαρισμός αναζήτησης"
              >
                <XIcon size={16} />
              </button>
            )}
          </div>
        </div>

        <div className="bg-slate-50 px-5 pt-1 pb-3 space-y-2">
          <div className="flex justify-between items-baseline text-2xl font-bold pt-1">
            <span className="font-heading">Σύνολο τραπεζιού</span>
            <span className="font-mono tabular" data-testid="order-total">
              {fmtEUR(totals.total)}
            </span>
          </div>
          {correctionsCount > 0 && (
            <div
              className="rounded-md border-2 border-amber-300 bg-amber-50 p-3 space-y-2"
              data-testid="corrections-action-bar"
            >
              <div className="flex items-center justify-between text-sm">
                <span className="font-bold text-amber-800 flex items-center gap-1.5">
                  <Pencil size={14} /> {correctionsCount} διόρθωσ{correctionsCount === 1 ? "η" : "εις"} σε εκκρεμότητα
                </span>
                {Math.abs(totals.correctionDelta) > 0.001 && (
                  <span className={`font-mono tabular text-sm font-bold ${totals.correctionDelta < 0 ? "text-red-700" : "text-emerald-700"}`}>
                    {totals.correctionDelta > 0 ? "+" : ""}{fmtEUR(totals.correctionDelta)}
                  </span>
                )}
              </div>
              <Button
                variant="outline"
                onClick={cancelAllCorrections}
                disabled={busy}
                className="w-full h-10 border-2 border-slate-300 text-slate-700 hover:bg-slate-100 font-bold"
                data-testid="corrections-cancel-btn"
              >
                <Undo2 size={14} className="mr-1.5" />
                Άκυρο διορθώσεων
              </Button>
              <div className="text-[11px] text-amber-700/80 italic text-center">
                Οι διορθώσεις θα σταλούν μαζί με τα νέα είδη στην «Καταχώρηση».
              </div>
            </div>
          )}
          {((tbl.submitted_batches || []).length > 0 || (tbl.open_items || []).length > 0) && (
            <div className="space-y-1 text-sm border-t border-slate-200 pt-2">
              {(tbl.submitted_batches || []).length > 0 && (
                <div className="flex justify-between text-slate-600">
                  <span>Καταχωρημένα ({(tbl.submitted_batches || []).length})</span>
                  <span className="font-mono tabular">{fmtEUR(totals.submitted)}</span>
                </div>
              )}
              {(tbl.open_items || []).length > 0 && (
                <div className="flex justify-between text-amber-700 font-bold">
                  <span>Νέα είδη</span>
                  <span className="font-mono tabular">{fmtEUR(totals.open)}</span>
                </div>
              )}
            </div>
          )}
          {/* Secondary actions (top): Ακύρωση & Επιστροφή (red, 2-line) | Εργασίες (popover με Μεταφορά / Προφόρμα / Μερική Πληρωμή) */}
          <div className="grid grid-cols-2 gap-2 pt-2">
            <Button
              className="h-14 bg-red-600 hover:bg-red-700 text-white font-bold leading-tight whitespace-normal px-2"
              onClick={cancelAndReturn}
              disabled={busy}
              data-testid="order-cancel-return-btn"
            >
              <XIcon size={16} className="mr-1.5 shrink-0" />
              <span className="flex flex-col items-start text-left">
                <span>Ακύρωση</span>
                <span>&amp; Επιστροφή</span>
              </span>
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="h-14 border-2 border-blue-300 text-blue-700 hover:bg-blue-50 font-bold leading-tight whitespace-normal px-2"
                  data-testid="order-tasks-btn"
                  disabled={busy}
                >
                  <Wrench size={16} className="mr-1.5 shrink-0" />
                  <span className="flex flex-col items-start text-left">
                    <span>Εργασίες</span>
                    <span className="text-[10px] font-normal opacity-70">πληρωμή · μεταφορά · προφόρμα · διαγραφή</span>
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                side="top"
                sideOffset={8}
                className="p-2 w-auto"
                style={{ width: "calc(var(--radix-popover-trigger-width) * 2 + 0.5rem + 1rem)" }}
                data-testid="order-tasks-popover"
              >
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    className="h-14 bg-violet-600 hover:bg-violet-700 text-white font-bold leading-tight whitespace-normal px-2"
                    onClick={() => guard("corrections", "Μερική πληρωμή", startPartialPayment)}
                    disabled={
                      busy ||
                      (tbl.open_items || []).length > 0 ||
                      correctionsCount > 0 ||
                      !(tbl.submitted_batches || []).some((b) =>
                        (b.items || []).some((it) => !it.corrected_deleted && !it.paid_partial),
                      )
                    }
                    data-testid="order-partial-pay-btn"
                  >
                    <HandCoins size={16} className="mr-1.5 shrink-0" />
                    <span className="flex flex-col items-start text-left">
                      <span>Μερική</span>
                      <span>Πληρωμή</span>
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-14 border-2 border-slate-300 text-slate-700 hover:bg-slate-100 font-bold leading-tight whitespace-normal px-2"
                    onClick={() => guard("transfer_table", "Μεταφορά τραπεζιού", startTransfer)}
                    data-testid="order-move-btn-popover"
                    disabled={
                      busy ||
                      (tbl.open_items || []).length > 0 ||
                      correctionsCount > 0
                    }
                    title={
                      (tbl.open_items || []).length > 0 || correctionsCount > 0
                        ? "Καταχωρήστε πρώτα τις αλλαγές πριν τη μεταφορά"
                        : undefined
                    }
                  >
                    <Move size={16} className="mr-1.5 shrink-0" />
                    <span className="flex flex-col items-start text-left">
                      <span>Μεταφορά</span>
                      <span>Τραπεζιού</span>
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-14 border-2 border-blue-300 text-blue-700 hover:bg-blue-50 font-bold leading-tight whitespace-normal px-2"
                    onClick={() => guard("can_print", "Εκτύπωση προφόρμας", printProforma)}
                    data-testid="order-proforma-btn-popover"
                    disabled={
                      busy ||
                      (tbl.open_items || []).length > 0 ||
                      correctionsCount > 0 ||
                      !(tbl.submitted_batches || []).some((b) =>
                        (b.items || []).some((it) => !it.corrected_deleted),
                      )
                    }
                  >
                    <Printer size={16} className="mr-1.5 shrink-0" />
                    <span className="flex flex-col items-start text-left">
                      <span>Εκτύπωση</span>
                      <span>Προφόρμας</span>
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-14 border-2 border-amber-300 text-amber-800 hover:bg-amber-50 font-bold leading-tight whitespace-normal px-2"
                    onClick={() => setQuickNoteOpen(true)}
                    data-testid="order-quicknote-btn"
                    disabled={busy}
                  >
                    <Pencil size={16} className="mr-1.5 shrink-0" />
                    <span className="flex flex-col items-start text-left">
                      <span>Σχόλια</span>
                      <span className="text-[10px] font-normal opacity-70">εκτύπωση σε ποστο</span>
                    </span>
                  </Button>
                  <Button
                    className="h-14 bg-red-600 hover:bg-red-700 text-white font-bold leading-tight whitespace-normal px-2"
                    onClick={() => guard("corrections", "Διαγραφή τραπεζιού", () => setDeleteTableConfirm(true))}
                    data-testid="order-delete-table-btn"
                    disabled={
                      busy ||
                      (tbl.open_items || []).length > 0 ||
                      correctionsCount > 0
                    }
                    title={
                      (tbl.open_items || []).length > 0 || correctionsCount > 0
                        ? "Καταχωρήστε πρώτα τις αλλαγές πριν τη διαγραφή"
                        : undefined
                    }
                  >
                    <Trash2 size={16} className="mr-1.5 shrink-0" />
                    <span className="flex flex-col items-start text-left">
                      <span>Διαγραφή</span>
                      <span>Τραπεζιού</span>
                    </span>
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Primary actions (bottom, bigger): Καταχώρηση | Απόδειξη */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              className="h-20 text-lg bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-sm"
              onClick={submitOrder}
              disabled={
                busy ||
                ((tbl.open_items || []).length === 0 &&
                  allUnsentSelections(tbl).length === 0 &&
                  correctionsCount === 0)
              }
              data-testid="order-send-btn"
            >
              <ArrowRight size={22} className="mr-2" /> Καταχώρηση
              {correctionsCount > 0 && (
                <span className="ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-300 text-amber-900 text-[11px] font-bold">
                  <Pencil size={10} /> {correctionsCount}
                </span>
              )}
            </Button>
            <Button
              className="h-20 text-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm"
              disabled={
                busy ||
                // Disable αν δεν υπάρχει τίποτα προς εξόφληση.
                (!(tbl.submitted_batches || []).some((b) =>
                  (b.items || []).some((it) => !it.corrected_deleted),
                ) &&
                  (tbl.open_items || []).length === 0) ||
                // Disable όσο υπάρχουν uncommitted αλλαγές (νέα είδη ή
                // pending corrections) — ο χρήστης πρέπει να πατήσει
                // πρώτα «Καταχώρηση», μετά γίνεται κλείσιμο.
                (tbl.open_items || []).length > 0 ||
                correctionsCount > 0
              }
              onClick={onCloseButtonClick}
              data-testid="order-close-btn"
              title={
                (tbl.open_items || []).length > 0 || correctionsCount > 0
                  ? "Καταχωρήστε πρώτα τις αλλαγές πριν το κλείσιμο"
                  : undefined
              }
            >
              <Euro size={22} className="mr-2" /> Απόδειξη
            </Button>
            {/* Μερική Πληρωμή — 3 stages: PICK → (optional WARN) → PAY */}
            <Dialog
              open={partialPayOpen}
              onOpenChange={(v) => {
                if (!v) {
                  setPartialPayOpen(false);
                  setPartialPayQty(new Map());
                  setPartialPayStage("pick");
                }
              }}
            >
              <DialogContent className="max-w-3xl w-[94vw] p-0">
                <DialogHeader className="px-5 pt-4 pb-3 border-b border-violet-200 bg-violet-50">
                  <DialogTitle className="text-xl font-bold flex items-center gap-2">
                    <HandCoins size={22} className="text-violet-700" />
                    {partialPayStage === "pick" && `Μερική Πληρωμή — Επιλογή ειδών (${tbl.name})`}
                    {partialPayStage === "warn" && "Προειδοποίηση — Είδη μη σταλμένα"}
                    {partialPayStage === "pay" && `Μερική Πληρωμή — Πληρωμή ${fmtEUR(partialPayTotal)}`}
                  </DialogTitle>
                </DialogHeader>

                {/* STAGE 1 — Item picker */}
                {partialPayStage === "pick" && (
                  <div className="p-5 space-y-2">
                    <div className="text-sm font-bold text-slate-700">
                      Σύνολο επιλεγμένων:{" "}
                      <span className="text-violet-700 font-mono">{fmtEUR(partialPayTotal)}</span>
                    </div>
                    <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
                      {(tbl.submitted_batches || []).map((b) => {
                        const payable = (b.items || [])
                          .map((it, i) => ({ ...it, _i: i }))
                          .filter((it) => !it.corrected_deleted && !it.paid_partial);
                        if (payable.length === 0) return null;
                        return (
                          <div key={b.batch_no} className="border-2 border-slate-200 rounded-lg p-3">
                            <div className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">
                              Παραγγελία #{b.batch_no}
                            </div>
                            {payable.map((it) => {
                              const key = `${b.batch_no}:${it._i}`;
                              const max = Number(it.qty);
                              const current = Number(partialPayQty.get(key) || 0);
                              const isFull = Math.abs(current - max) < 1e-6;
                              const isPartial = current > 0 && !isFull;
                              const setQty = (q) => {
                                const clamped = Math.max(0, Math.min(Number(q) || 0, max));
                                setPartialPayQty((prev) => {
                                  const next = new Map(prev);
                                  if (clamped <= 0) next.delete(key);
                                  else next.set(key, clamped);
                                  return next;
                                });
                              };
                              return (
                                <div
                                  key={key}
                                  className={`flex items-center gap-3 px-3 py-3 rounded-md mb-1 border-2 ${
                                    isFull
                                      ? "bg-violet-50 border-violet-400"
                                      : isPartial
                                      ? "bg-amber-50 border-amber-400"
                                      : "border-transparent hover:bg-slate-50"
                                  }`}
                                  data-testid={`partial-pay-item-${b.batch_no}-${it._i}`}
                                >
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      type="button"
                                      onClick={() => setQty(current - 1)}
                                      disabled={current <= 0}
                                      className="w-9 h-9 rounded-md border-2 border-slate-300 grid place-items-center hover:bg-slate-100 disabled:opacity-30"
                                    >
                                      <Minus size={14} />
                                    </button>
                                    <div className="w-12 h-9 text-center font-mono font-bold border-2 border-slate-300 rounded-md grid place-items-center bg-white">
                                      {current}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => setQty(current + 1)}
                                      disabled={current >= max}
                                      className="w-9 h-9 rounded-md border-2 border-slate-300 grid place-items-center hover:bg-slate-100 disabled:opacity-30"
                                    >
                                      <Plus size={14} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setQty(max)}
                                      className="ml-1 h-9 px-2 text-xs font-bold rounded-md border-2 border-slate-300 hover:bg-slate-100"
                                    >
                                      /{max}
                                    </button>
                                  </div>
                                  <div className="flex-1 font-bold text-slate-800 truncate">
                                    {it.description}
                                    {!it.sent_to_register && (
                                      <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                        μη σταλμένο
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm font-mono text-slate-600">
                                    {fmtEUR(Number(it.price || 0) + Number(it.modifier_total || 0))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-200">
                      <Button
                        variant="outline"
                        onClick={() => setPartialPayOpen(false)}
                        className="h-14 font-bold border-2"
                        data-testid="partial-pay-cancel"
                      >
                        Άκυρο
                      </Button>
                      <Button
                        onClick={confirmPartialPick}
                        disabled={partialPayQty.size === 0}
                        className="h-14 bg-violet-600 hover:bg-violet-700 text-white font-bold text-base"
                        data-testid="partial-pay-confirm-pick"
                      >
                        Πληρωμή <ArrowRight size={18} className="ml-2" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* STAGE 2 — Warning for unsent items */}
                {partialPayStage === "warn" && (
                  <div className="p-5 space-y-4">
                    <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-md text-sm">
                      <div className="font-bold text-amber-900 mb-2">
                        ⚠️ Τα προϊόντα δεν έχουν αποσταλεί στην ταμειακή
                      </div>
                      <p className="text-slate-700 leading-relaxed">
                        Κάποια από τα επιλεγμένα είδη είναι μόνο στο πρόγραμμα.
                        Θέλεις να τα στείλεις στην ταμειακή ώστε να εκδοθεί
                        κανονική απόδειξη (με όλους τους τρόπους πληρωμής), ή
                        να εισπράξεις μόνο μετρητά χωρίς απόδειξη;
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        onClick={() => {
                          setPartialPayCashOnly(true);
                          setPartialPayStage("pay");
                          // Pre-fill split with the configured «Μετρητά»
                          // payment method using its payment_id (what the
                          // backend's CartPayment model expects).
                          const cash =
                            (payments || []).find((p) => /ΜΕΤΡΗΤ/i.test(p.name || "")) ||
                            (payments || []).find((p) => p.payment_id === 1) ||
                            (payments || [])[0];
                          if (cash) {
                            setPartialPaySplit([
                              { payment_id: cash.payment_id, amount: String(partialPayTotal.toFixed(2)) },
                            ]);
                          }
                        }}
                        variant="outline"
                        className="h-20 border-2 font-bold flex-col"
                        data-testid="partial-pay-cash-only"
                      >
                        <span className="text-base">Όχι — Μόνο Μετρητά</span>
                        <span className="text-xs font-normal opacity-70 mt-1">
                          χωρίς απόδειξη
                        </span>
                      </Button>
                      <Button
                        onClick={() => {
                          setPartialPayCashOnly(false);
                          setPartialPayStage("pay");
                        }}
                        className="h-20 bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex-col"
                        data-testid="partial-pay-send-yes"
                      >
                        <span className="text-base">Ναι — Στείλε στην Ταμειακή</span>
                        <span className="text-xs font-normal opacity-90 mt-1">
                          κανονική απόδειξη
                        </span>
                      </Button>
                    </div>
                    <Button
                      variant="ghost"
                      onClick={() => setPartialPayStage("pick")}
                      className="w-full"
                    >
                      ← Πίσω στην επιλογή
                    </Button>
                  </div>
                )}

                {/* STAGE 3 — Payment dialog with chosen items */}
                {partialPayStage === "pay" && (
                  <div className="p-5">
                    <PaymentDialogBody
                      total={partialPayTotal}
                      payments={
                        partialPayCashOnly
                          ? (payments || []).filter((p) =>
                              /ΜΕΤΡΗΤ/i.test(p.name || ""),
                            )
                          : payments
                      }
                      split={partialPaySplit}
                      setSplit={setPartialPaySplit}
                      comment={partialPayComment}
                      setComment={setPartialPayComment}
                      nonFiscal=""
                      setNonFiscal={() => {}}
                      busy={busy}
                      settings={settings}
                      cartEmpty={partialPayTotal === 0}
                      onPrintOrder={() => {}}
                      onSubmit={submitPartialPayment}
                      onSaveNonFiscal={() => {}}
                      printers={printers}
                      printOverride={printOverride}
                      setPrintOverride={setPrintOverride}
                    />
                    <Button
                      variant="ghost"
                      onClick={() => setPartialPayStage("pick")}
                      className="w-full mt-2"
                    >
                      ← Πίσω στην επιλογή ειδών
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            <Dialog open={closeOpen} onOpenChange={(v) => { setCloseOpen(v); if (!v) setRecentlyFlushed(0); }}>
              <DialogContent className="max-w-4xl w-[96vw] p-0 overflow-hidden">
                <DialogHeader className="px-5 pt-5 pb-2 bg-emerald-50 border-b border-emerald-200">
                  <DialogTitle className="text-lg font-bold">
                    Πληρωμή — {tbl.name} (#{tbl.device_number})
                  </DialogTitle>
                  <div className="text-xs text-slate-600 mt-1">
                    Σύνολο όλων των παραγγελιών:{" "}
                    <span className="font-bold font-mono">{fmtEUR(totals.total)}</span>
                  </div>
                  {recentlyFlushed > 0 && (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-800 text-xs font-bold w-fit animate-pulse"
                      data-testid="flush-success-badge"
                    >
                      <CheckSquare size={13} />
                      ✓ Στάλθηκαν {recentlyFlushed} είδ{recentlyFlushed === 1 ? "ος" : "η"} στην ταμειακή
                    </div>
                  )}
                </DialogHeader>
                <div className="p-5">
                  <PaymentDialogBody
                    total={totals.total}
                    payments={payments}
                    split={closePayments}
                    setSplit={setClosePayments}
                    comment={closeComment}
                    setComment={setCloseComment}
                    nonFiscal={closeNonFiscal}
                    setNonFiscal={setCloseNonFiscal}
                    busy={busy}
                    settings={settings}
                    cartEmpty={totals.total === 0}
                    onPrintOrder={printProforma}
                    onSubmit={closeTable}
                    onSaveNonFiscal={() => toast.info("Τα είδη είναι ήδη αποθηκευμένα στο τραπέζι")}
                    printers={printers}
                    printOverride={printOverride}
                    setPrintOverride={setPrintOverride}
                  />
                </div>
              </DialogContent>
            </Dialog>
            {/* Pending-flush warning dialog — appears when «Κλείσιμο» is
                pressed while there are still standby items not yet sent
                to the cash register. */}
            <Dialog open={pendingFlushOpen} onOpenChange={setPendingFlushOpen}>
              <DialogContent className="max-w-xl w-[94vw]">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold flex items-center gap-2 text-amber-700">
                    <Receipt size={22} /> Υπάρχουν είδη σε εκκρεμότητα
                  </DialogTitle>
                  <DialogDescription className="text-sm text-slate-600 pt-2 leading-relaxed">
                    Το τραπέζι «{tbl.name}» έχει είδη που δεν έχουν σταλεί ακόμη στην ταμειακή.
                    Για να εκδοθεί τελική απόδειξη πρέπει πρώτα να σταλούν όλα τα είδη.
                  </DialogDescription>
                </DialogHeader>
                {(() => {
                  // Build flat list of standby items across all batches +
                  // any leftover open_items so the operator can review what
                  // will get pushed to the register.
                  const rows = [];
                  let pendingTotal = 0;
                  for (const b of tbl.submitted_batches || []) {
                    for (const it of b.items || []) {
                      if (it.sent_to_register || it.corrected_deleted) continue;
                      const unit = Number(it.price || 0) + Number(it.modifier_total || 0);
                      const gross = Number(it.qty || 0) * unit;
                      const net = it.discount_percent
                        ? gross * (1 - Number(it.discount_percent) / 100)
                        : gross;
                      pendingTotal += net;
                      rows.push({
                        source: `Παρ. #${b.batch_no}`,
                        description: it.description,
                        qty: it.qty,
                        net,
                        comment: it.comment || "",
                        discount: it.discount_percent || 0,
                      });
                    }
                  }
                  for (const it of tbl.open_items || []) {
                    const unit = Number(it.price || 0) + Number(it.modifier_total || 0);
                    const net = Number(it.qty || 0) * unit;
                    pendingTotal += net;
                    rows.push({
                      source: "Νέα είδη",
                      description: it.description,
                      qty: it.qty,
                      net,
                      comment: it.comment || "",
                      discount: 0,
                    });
                  }
                  return (
                    <div className="mt-3 rounded-lg border-2 border-amber-200 bg-amber-50 overflow-hidden">
                      <div className="px-3 py-2 bg-amber-100 border-b border-amber-200 flex items-center justify-between">
                        <span className="text-xs uppercase tracking-wider text-amber-800 font-bold">
                          {rows.length} είδη σε αναμονή
                        </span>
                        <span className="text-base font-bold font-mono tabular text-amber-900">
                          {fmtEUR(pendingTotal)}
                        </span>
                      </div>
                      <ul className="max-h-56 overflow-y-auto divide-y divide-amber-100">
                        {rows.map((row, i) => (
                          <li
                            key={i}
                            className="flex items-start justify-between px-3 py-2 text-sm bg-white"
                            data-testid={`pending-flush-row-${i}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-slate-800">
                                {row.qty} × {row.description}
                                {row.discount ? (
                                  <span className="ml-1 text-xs font-mono text-red-600">-{row.discount}%</span>
                                ) : null}
                              </div>
                              {row.comment && (
                                <div className="text-xs italic text-blue-700 truncate">
                                  › {row.comment}
                                </div>
                              )}
                              <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">
                                {row.source}
                              </div>
                            </div>
                            <div className="font-mono tabular font-bold text-slate-700 shrink-0 ml-2">
                              {fmtEUR(row.net)}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}
                <div className="flex flex-col gap-2 mt-4">
                  <label
                    className="flex items-center gap-2 px-3 py-2 rounded-md border-2 border-slate-200 hover:bg-slate-50 cursor-pointer select-none"
                    data-testid="flush-print-proforma-label"
                  >
                    <input
                      type="checkbox"
                      checked={flushPrintProforma}
                      onChange={(e) => setFlushPrintProforma(e.target.checked)}
                      className="w-5 h-5 accent-blue-600"
                      data-testid="flush-print-proforma"
                    />
                    <Printer size={16} className="text-blue-600" />
                    <span className="text-sm font-bold text-slate-700">
                      Εκτύπωση προφόρμας πριν την έκδοση
                    </span>
                  </label>
                  <Button
                    onClick={handleFlushAndClose}
                    disabled={busy}
                    className="h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base"
                    data-testid="pending-flush-confirm"
                  >
                    <Send size={18} className="mr-2" />
                    Άμεση Αποστολή & Έκδοση Τελικής Απόδειξης
                  </Button>
                  <Button
                    onClick={() => {
                      // Pivot to the AA/BB split flow. The warn dialog
                      // closes immediately so the ContinuePaymentDialog
                      // sits on top of the table screen.
                      setPendingFlushOpen(false);
                      setFlushPrintProforma(false);
                      setContinuePayOpen(true);
                    }}
                    disabled={busy}
                    className="h-14 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-base"
                    data-testid="pending-flush-continue-payment"
                  >
                    <HandCoins size={18} className="mr-2" />
                    ΣΥΝΕΧΙΣΗ ΕΙΣΠΡΑΞΗΣ (Ταμείο ΑΑ / ΒΒ)
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setPendingFlushOpen(false); setFlushPrintProforma(false); }}
                    disabled={busy}
                    className="h-11 border-2 font-bold"
                    data-testid="pending-flush-cancel"
                  >
                    Ακύρωση
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>

      {/* Product grid + categories + favourites — shared with POSRetail */}
      {/* When the comment selector is active we REPLACE the catalogue with an
          inline comment panel that mirrors the same layout (category strip
          becomes prefix bar, product grid becomes comment-tile grid). Tiles
          stay the same physical size as products so the muscle memory is
          preserved. */}
      {commentDialog.open ? (
        <CommentInlinePanel
          tbl={tbl}
          commentDialog={commentDialog}
          setCommentDialog={setCommentDialog}
          comments={comments}
          commentGroups={commentGroups}
          settings={settings}
          COMMENT_PREFIXES={COMMENT_PREFIXES}
          togglePrefix={togglePrefix}
          toggleCommentTile={toggleCommentTile}
          incrementSplit={incrementSplit}
          decrementSplit={decrementSplit}
          saveComment={saveComment}
          oskOpen={oskOpen}
          setOskOpen={setOskOpen}
        />
      ) : (
        <ProductCatalogPanel
          products={products}
          categories={categories}
          activeCat={activeCat}
          setActiveCat={setActiveCat}
          settings={settings}
          onProductClick={addProduct}
          testidPrefix="rest"
          categoryBorderClass="border-b border-slate-200"
          searchValue={search}
        />
      )}

      {/* ---- Cancel & return confirmation dialog ---- */}
      <ConfirmDialog
        open={cancelConfirmOpen}
        onOpenChange={setCancelConfirmOpen}
        title="Ακύρωση & Επιστροφή"
        description="Τα νέα είδη που δεν έχουν καταχωρηθεί θα διαγραφούν οριστικά. Τα ήδη καταχωρημένα παραμένουν ανέπαφα."
        subject={tbl?.name}
        rows={[
          {
            label: "Τμχ προς διαγραφή",
            value: (tbl?.open_items || []).reduce((s, it) => s + Number(it.qty || 0), 0),
          },
          {
            label: "Σύνολο νέων ειδών",
            value: fmtEUR(totals.open),
            accent: "red",
          },
        ]}
        confirmLabel="Ναι, διαγραφή"
        cancelLabel="Όχι, επιστροφή"
        onConfirm={confirmCancelAndReturn}
        busy={busy}
        testidPrefix="cancel-confirm"
      />

      {/* ---- ΔΙΑΓΡΑΦΗ ΤΡΑΠΕΖΙΟΥ — confirm dialog ---- */}
      <ConfirmDialog
        open={deleteTableConfirm}
        onOpenChange={setDeleteTableConfirm}
        title="Διαγραφή Τραπεζιού"
        description="Όλα τα είδη θα ακυρωθούν στην ταμειακή με 2 αποδείξεις ακύρωσης και το τραπέζι θα αδειάσει."
        subject={tbl?.name}
        rows={[
          {
            label: "Καταχωρημένες παραγγελίες",
            value: (tbl?.submitted_batches || []).length,
          },
          {
            label: "Σύνολο τραπεζιού",
            value: fmtEUR(totals.total),
            accent: "red",
          },
        ]}
        confirmLabel="Ναι, διαγραφή τραπεζιού"
        cancelLabel="Όχι"
        onConfirm={requestDeleteTable}
        busy={busy}
        testidPrefix="delete-table-confirm"
      />

      {/* ---- Last-item void → auto delete-table warning ---- */}
      <ConfirmDialog
        open={deleteTableLastItem}
        onOpenChange={setDeleteTableLastItem}
        title="Διαγραφή τελευταίου είδους"
        description="Αυτό είναι το τελευταίο είδος του τραπεζιού. Η ακύρωσή του θα ενεργοποιήσει τη ΔΙΑΓΡΑΦΗ ΤΡΑΠΕΖΙΟΥ (2 αποδείξεις ακύρωσης + καθάρισμα)."
        subject={tbl?.name}
        rows={[
          {
            label: "Είδη προς ακύρωση",
            value: correctionsCount,
          },
        ]}
        confirmLabel="Ναι, διαγραφή τραπεζιού"
        cancelLabel="Όχι"
        onConfirm={requestDeleteTable}
        busy={busy}
        testidPrefix="delete-table-last-item"
      />

      {/* ---- Force delete (τραπέζι σταλμένο στην ταμειακή) — προειδοποίηση + κωδικός διαχειριστή ---- */}
      <Dialog
        open={deleteForce.open}
        onOpenChange={(o) => !o && setDeleteForce({ open: false, code: "" })}
      >
        <DialogContent className="max-w-md" data-testid="delete-force-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle size={18} /> Διαγραφή Τραπεζιού «{tbl?.name}»
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-700 leading-relaxed">
              Η διαδικασία αυτή μπορεί να φέρει αστάθεια στο πρόγραμμα. Μετά την
              διαγραφή παρακαλώ ελέγξτε την κατάσταση του τραπεζιού στην ταμειακή.
              Για να ολοκληρωθεί η διαγραφή βάλτε τον κωδικό Διαχειριστή.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div
              className="mt-1 rounded-lg border-2 border-slate-300 bg-slate-50 p-3 text-center font-mono text-3xl tracking-[0.4em] text-slate-800 select-none min-h-[52px] flex items-center justify-center"
              data-testid="delete-force-code-display"
            >
              {deleteForce.code
                ? "•".repeat(deleteForce.code.length)
                : <span className="text-slate-400 text-lg tracking-normal">Κωδικός Διαχειριστή</span>}
            </div>
            <div className="grid grid-cols-3 gap-2" data-testid="delete-force-numpad">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => (
                <button
                  type="button"
                  key={k}
                  onClick={() => setDeleteForce((d) => ({ ...d, code: (d.code + k).slice(0, 20) }))}
                  data-testid={`delete-force-key-${k}`}
                  className="h-14 rounded-lg border-2 border-slate-300 bg-white hover:bg-slate-100 active:scale-95 active:bg-slate-200 text-2xl font-bold text-slate-800 transition-all shadow-sm"
                >
                  {k}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setDeleteForce((d) => ({ ...d, code: "" }))}
                data-testid="delete-force-key-clear"
                className="h-14 rounded-lg border-2 border-amber-300 bg-amber-50 hover:bg-amber-100 active:scale-95 text-sm font-bold text-amber-800 transition-all shadow-sm"
              >
                ΚΑΘΑΡ.
              </button>
              <button
                type="button"
                onClick={() => setDeleteForce((d) => ({ ...d, code: (d.code + "0").slice(0, 20) }))}
                data-testid="delete-force-key-0"
                className="h-14 rounded-lg border-2 border-slate-300 bg-white hover:bg-slate-100 active:scale-95 active:bg-slate-200 text-2xl font-bold text-slate-800 transition-all shadow-sm"
              >
                0
              </button>
              <button
                type="button"
                onClick={() => setDeleteForce((d) => ({ ...d, code: d.code.slice(0, -1) }))}
                data-testid="delete-force-key-backspace"
                className="h-14 rounded-lg border-2 border-slate-300 bg-slate-100 hover:bg-slate-200 active:scale-95 text-lg font-bold text-slate-700 transition-all shadow-sm"
              >
                ⌫
              </button>
            </div>
          </div>
          <DialogFooter className="grid grid-cols-2 gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setDeleteForce({ open: false, code: "" })}
              disabled={busy}
              data-testid="delete-force-cancel"
            >
              Ακύρωση
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
              onClick={() => runDeleteTable(deleteForce.code)}
              disabled={busy || !deleteForce.code}
              data-testid="delete-force-confirm"
            >
              Διαγραφή Τραπεζιού
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Comment selector renders inline above (CommentInlinePanel) — replaces the catalogue area. */}

      {/* ---- Submit choice dialog (3 options) ---- */}
      <Dialog open={submitChoiceOpen} onOpenChange={setSubmitChoiceOpen}>
        <DialogContent hideClose className="max-w-2xl w-[92vw] p-0">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200">
            <DialogTitle className="text-2xl font-bold">Καταχώρηση Παραγγελίας — {tbl?.name}</DialogTitle>
            <DialogDescription className="text-base text-slate-600 mt-1">
              Επιλέξτε τρόπο καταχώρησης (touch-friendly)
            </DialogDescription>
          </DialogHeader>
          <div className="p-5 space-y-3">
            <ChoiceButton
              icon={Send}
              title="Αποστολή στην Ταμειακή"
              desc="Καταχώρηση + όλα τα ασταλέντα είδη μεταφέρονται αυτόματα στην ταμειακή."
              onClick={handleFullSendToRegister}
              disabled={busy}
              testid="submit-choice-full"
              color="bg-emerald-600 hover:bg-emerald-700"
            />
            <ChoiceButton
              icon={ListChecks}
              title="Μερική Αποστολή"
              desc="Επιλογή συγκεκριμένων ειδών για αποστολή στην ταμειακή. Αν δεν επιλεχθεί τίποτα, τα είδη απλά αποθηκεύονται στο τραπέζι."
              onClick={handlePartialSendStart}
              disabled={busy}
              testid="submit-choice-partial"
              color="bg-blue-600 hover:bg-blue-700"
            />
          </div>
          <div className="px-5 pb-5 pt-2 border-t border-slate-200 bg-slate-50">
            <Button
              variant="outline"
              onClick={() => setSubmitChoiceOpen(false)}
              data-testid="submit-choice-cancel"
              className="w-full h-14 text-base font-bold border-2"
            >
              Ακύρωση
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---- Partial-send dialog: checkbox list of unsent items ---- */}
      <Dialog open={partialOpen} onOpenChange={(v) => { if (!v) { setPartialOpen(false); setPartialQty(new Map()); } }}>
        <DialogContent hideClose className="max-w-3xl w-[94vw] p-0">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200">
            <DialogTitle className="text-2xl font-bold">
              Μερική Αποστολή στην Ταμειακή — {tbl?.name}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Επιλογή τμχ ανά είδος για αποστολή στην ταμειακή — μπορείτε να στείλετε μέρος της ποσότητας.
            </DialogDescription>
          </DialogHeader>
          {tbl && (() => {
            const all = allUnsentSelections(tbl);
            const virtualOpen = (tbl.open_items || []);
            // Συνολικά τμχ διαθέσιμα + επιλεγμένα τμχ (όχι γραμμές).
            let totalAvail = 0;
            let totalSelected = 0;
            for (const s of all) {
              const it = (tbl.submitted_batches.find((b) => b.batch_no === s.batch_no))?.items?.[s.item_idx];
              if (!it) continue;
              totalAvail += Number(it.qty || 0);
              const q = partialQty.get(`${s.batch_no}:${s.item_idx}`);
              totalSelected += q ? Number(q) : 0;
            }
            virtualOpen.forEach((it, i) => {
              totalAvail += Number(it.qty || 0);
              const q = partialQty.get(`new:${i}`);
              totalSelected += q ? Number(q) : 0;
            });
            const totalLines = all.length + virtualOpen.length;
            const allFull = totalAvail > 0 && Math.abs(totalSelected - totalAvail) < 1e-6;
            const setQtyFor = (key, qty, max) => {
              const clamped = Math.max(0, Math.min(Number(qty) || 0, max));
              setPartialQty((prev) => {
                const next = new Map(prev);
                if (clamped <= 0) next.delete(key);
                else next.set(key, clamped);
                return next;
              });
            };
            return (
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-bold text-slate-700">
                    {totalLines} είδη · {totalAvail} τμχ διαθέσιμα · <span className="text-blue-700">{totalSelected} επιλεγμένα</span>
                  </div>
                  <Button
                    size="lg"
                    variant={allFull ? "default" : "outline"}
                    onClick={() => {
                      if (allFull) {
                        setPartialQty(new Map());
                      } else {
                        const next = new Map();
                        for (const s of all) {
                          const it = tbl.submitted_batches.find((b) => b.batch_no === s.batch_no)?.items?.[s.item_idx];
                          if (it) next.set(`${s.batch_no}:${s.item_idx}`, Number(it.qty));
                        }
                        virtualOpen.forEach((it, i) => {
                          next.set(`new:${i}`, Number(it.qty));
                        });
                        setPartialQty(next);
                      }
                    }}
                    data-testid="partial-toggle-all"
                    className="h-12 px-5 font-bold"
                  >
                    {allFull ? <CheckSquare size={18} className="mr-2" /> : <Square size={18} className="mr-2" />}
                    Όλα
                  </Button>
                </div>
                <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
                  {/* Virtual «νέα είδη» batch — open_items που ΔΕΝ έχουν
                      ακόμα γίνει persisted ως κανονικό batch. Αν επιλέξει
                      ο χρήστης κάτι από αυτά, στο confirm θα γίνει
                      ένα `persistAsBatch()` + kitchen print + send. Αν
                      ακυρώσει, μένουν στο cart αμετάβλητα. */}
                  {virtualOpen.length > 0 && (
                    <div className="border-2 border-emerald-300 rounded-lg p-3 bg-emerald-50/40">
                      <div className="text-xs font-bold text-emerald-700 mb-2 uppercase tracking-wider flex items-center gap-1.5">
                        <Plus size={12} /> Νέα είδη (δεν έχουν σταλεί ακόμα στην κουζίνα)
                      </div>
                      {virtualOpen.map((it, i) => {
                        const key = `new:${i}`;
                        const max = Number(it.qty);
                        const current = Number(partialQty.get(key) || 0);
                        const isFull = Math.abs(current - max) < 1e-6;
                        const isPartial = current > 0 && !isFull;
                        return (
                          <div
                            key={key}
                            className={`flex items-center gap-3 px-3 py-3 rounded-md mb-1 border-2 ${
                              isFull
                                ? "bg-blue-50 border-blue-400"
                                : isPartial
                                ? "bg-amber-50 border-amber-400"
                                : "border-transparent hover:bg-white hover:border-emerald-200"
                            }`}
                            data-testid={`partial-item-new-${i}`}
                          >
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => setQtyFor(key, current - 1, max)}
                                disabled={current <= 0}
                                className="w-9 h-9 rounded-md border-2 border-slate-300 grid place-items-center hover:bg-slate-100 disabled:opacity-30"
                                data-testid={`partial-item-new-${i}-dec`}
                              >
                                <Minus size={14} />
                              </button>
                              <div
                                className="w-12 h-9 text-center font-mono font-bold border-2 border-slate-300 rounded-md grid place-items-center bg-white"
                                data-testid={`partial-item-new-${i}-qty`}
                              >
                                {current}
                              </div>
                              <button
                                type="button"
                                onClick={() => setQtyFor(key, current + 1, max)}
                                disabled={current >= max}
                                className="w-9 h-9 rounded-md border-2 border-slate-300 grid place-items-center hover:bg-slate-100 disabled:opacity-30"
                                data-testid={`partial-item-new-${i}-inc`}
                              >
                                <Plus size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setQtyFor(key, max, max)}
                                className="ml-1 h-9 px-2 text-xs font-bold rounded-md border-2 border-slate-300 hover:bg-slate-100"
                                data-testid={`partial-item-new-${i}-max`}
                              >
                                /{max}
                              </button>
                            </div>
                            <div className="flex-1 font-bold text-slate-800 truncate">
                              {it.description}
                            </div>
                            <div className="text-sm font-mono text-slate-600">
                              {fmtEUR(Number(it.price || 0) + Number(it.modifier_total || 0))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {(tbl.submitted_batches || []).map((b) => {
                    const unsentInBatch = (b.items || [])
                      .map((it, i) => ({ ...it, _i: i }))
                      .filter((it) => !it.sent_to_register && !it.corrected_deleted);
                    if (unsentInBatch.length === 0) return null;
                    return (
                      <div key={b.batch_no} className="border-2 border-slate-200 rounded-lg p-3">
                        <div className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">
                          Παραγγελία #{b.batch_no} ·{" "}
                          {new Date(b.timestamp).toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                        {unsentInBatch.map((it) => {
                          const key = `${b.batch_no}:${it._i}`;
                          const max = Number(it.qty);
                          const current = Number(partialQty.get(key) || 0);
                          const isFull = Math.abs(current - max) < 1e-6;
                          const isPartial = current > 0 && !isFull;
                          return (
                            <div
                              key={key}
                              className={`flex items-center gap-3 px-3 py-3 rounded-md mb-1 border-2 ${
                                isFull
                                  ? "bg-blue-50 border-blue-400"
                                  : isPartial
                                  ? "bg-amber-50 border-amber-400"
                                  : "border-transparent hover:bg-slate-50 hover:border-slate-200"
                              }`}
                              data-testid={`partial-item-${b.batch_no}-${it._i}`}
                            >
                              <div className="flex items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => setQtyFor(key, current - 1, max)}
                                  disabled={current <= 0}
                                  className="w-10 h-10 rounded-md border-2 border-slate-300 bg-white hover:bg-red-50 active:scale-95 transition-all text-red-600 font-bold disabled:opacity-40 flex items-center justify-center"
                                  data-testid={`partial-item-${b.batch_no}-${it._i}-dec`}
                                >
                                  <Minus size={18} />
                                </button>
                                <input
                                  type="number"
                                  min={0}
                                  max={max}
                                  value={current || ""}
                                  placeholder="0"
                                  onChange={(e) => setQtyFor(key, e.target.value, max)}
                                  className="w-14 h-10 text-center text-lg font-bold font-mono tabular rounded-md border-2 border-slate-300 focus:border-blue-400 focus:outline-none"
                                  data-testid={`partial-item-${b.batch_no}-${it._i}-qty`}
                                />
                                <button
                                  type="button"
                                  onClick={() => setQtyFor(key, current + 1, max)}
                                  disabled={current >= max}
                                  className="w-10 h-10 rounded-md border-2 border-slate-300 bg-white hover:bg-emerald-50 active:scale-95 transition-all text-emerald-600 font-bold disabled:opacity-40 flex items-center justify-center"
                                  data-testid={`partial-item-${b.batch_no}-${it._i}-inc`}
                                >
                                  <Plus size={18} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setQtyFor(key, max, max)}
                                  className="ml-1 h-10 px-2 rounded-md border-2 border-slate-300 bg-white hover:bg-blue-50 active:scale-95 transition-all text-blue-600 font-bold text-xs"
                                  data-testid={`partial-item-${b.batch_no}-${it._i}-max`}
                                  title="Επιλογή όλων των τμχ της γραμμής"
                                >
                                  /{max}
                                </button>
                              </div>
                              <div className="flex-1 text-base font-bold truncate ml-2">
                                {it.description}
                                {it.comment && (
                                  <span className="block text-xs italic text-blue-700 truncate">
                                    › {it.comment}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-slate-500 font-mono tabular shrink-0">
                                {fmtEUR(it.price)} ανά τμχ
                              </div>
                              <div className="text-base font-mono font-bold tabular w-24 text-right shrink-0">
                                {fmtEUR(current * Number(it.price))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                  {all.length === 0 && virtualOpen.length === 0 && (
                    <div className="text-center text-slate-400 py-10 text-sm">
                      Όλα τα είδη έχουν ήδη σταλεί στην ταμειακή.
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          <div className="px-5 pb-5 pt-3 border-t border-slate-200 bg-slate-50 grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setPartialOpen(false);
                setPartialQty(new Map());
              }}
              data-testid="partial-cancel"
              className="h-14 text-base font-bold border-2"
            >
              Ακύρωση
            </Button>
            <Button
              onClick={handlePartialConfirm}
              disabled={busy}
              className="h-14 text-base font-bold bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="partial-confirm"
            >
              <Send size={18} className="mr-2" />
              {(() => {
                const totalQty = Array.from(partialQty.values()).reduce(
                  (s, q) => s + Number(q || 0), 0,
                );
                // Iter88: κενή επιλογή → «Αποθήκευση» (απλή καταχώρηση,
                // χωρίς αποστολή στην ταμειακή). Με επιλογές → «Αποστολή».
                return totalQty === 0
                  ? "Αποθήκευση (χωρίς ταμειακή)"
                  : `Αποστολή (${totalQty} τμχ)`;
              })()}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* ---- Numpad (qty/price/disc) — appears when item button is tapped ---- */}
      <Numpad
        open={numpad.open}
        label={numpad.label}
        value={numpad.value}
        allowDot={numpad.allowDot}
        onChange={setNumpadValue}
        onAccept={onNumpadAccept}
        onClose={closeNumpad}
      />
      {/* ---- Floating «Έλεγχος Ανοιχτών Τραπεζιών» panel ---- */}
      <OpenTablesCheckPanel
        open={openTablesCheck}
        onClose={() => setOpenTablesCheck(false)}
        onPrintAll={handleAllOpenTables}
        onCheckOne={checkSingleTable}
        onSecretBulkFlush={() => setBulkFlushWarn(true)}
      />
      {/* ---- Alphanumeric on-screen keyboard for the free-text comment ---- */}
      <OnScreenKeyboard
        open={oskOpen}
        title="Ελεύθερο σχόλιο"
        initialValue={commentDialog.freeText || ""}
        suggestionsKey="restaurant-freetext"
        suggestionsBackend={true}
        onConfirm={(val) => {
          setCommentDialog((d) => ({ ...d, freeText: val }));
          setOskOpen(false);
        }}
        onClose={() => setOskOpen(false)}
      />

      {/* ---- Reprint preview dialog ---- */}
      <Dialog
        open={reprintPreview.open}
        onOpenChange={(o) => !o && setReprintPreview({ open: false, batchNo: null, tickets: [], loading: false })}
      >
        <DialogContent className="max-w-3xl" data-testid="reprint-preview-dialog">
          <DialogHeader>
            <DialogTitle>Προεπισκόπηση εκτύπωσης — Παραγγελία #{reprintPreview.batchNo}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {reprintPreview.loading && <div className="text-center text-slate-500 py-6">Φόρτωση…</div>}
            {!reprintPreview.loading && reprintPreview.tickets.length === 0 && (
              <div className="text-center text-slate-400 py-6">
                Δεν υπάρχουν χαρτάκια προς εκτύπωση για αυτή την παραγγελία.
              </div>
            )}
            {reprintPreview.tickets.map((t) => (
              <div key={t.printer_id} className="border-2 border-slate-200 rounded-lg overflow-hidden" data-testid={`reprint-ticket-${t.printer_name}`}>
                <div className="bg-slate-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                  <Printer size={14} /> {t.printer_name}
                </div>
                <pre className="font-mono text-xs whitespace-pre-wrap p-3 bg-white max-h-64 overflow-y-auto">
                  {t.text}
                </pre>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReprintPreview({ open: false, batchNo: null, tickets: [], loading: false })}
              data-testid="reprint-preview-close"
            >
              Κλείσιμο
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold"
              onClick={confirmReprint}
              disabled={reprintPreview.loading || reprintPreview.tickets.length === 0}
              data-testid="reprint-preview-confirm"
            >
              <Printer size={14} className="mr-2" /> Στείλε στους εκτυπωτές
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Free-text comment editor for items without comment groups. */}
      <SimpleCommentDialog
        open={simpleComment.open}
        onOpenChange={(o) => setSimpleComment((s) => ({ ...s, open: o, idx: o ? s.idx : null }))}
        item={(() => {
          if (!tbl || simpleComment.idx == null) return null;
          if (simpleComment.source === "submitted") return null;
          return (tbl.open_items || [])[simpleComment.idx] || null;
        })()}
        initialText={(() => {
          if (!tbl || simpleComment.idx == null) return "";
          const it = (tbl.open_items || [])[simpleComment.idx];
          return it?.free_text || it?.comment || "";
        })()}
        onSave={(text) => {
          if (!tbl || simpleComment.idx == null) return;
          const trimmed = (text || "").trim();
          const next = (tbl.open_items || []).map((it, i) =>
            i === simpleComment.idx
              ? { ...it, free_text: trimmed || null, comment: trimmed, comments: [] }
              : it,
          );
          updateTable({ ...tbl, open_items: next });
          // Feb 2026 — local-only cart; no PUT until Αποστολή/Αποθήκευση.
        }}
      />

      {/* «ΣΥΝΕΧΙΣΗ ΕΙΣΠΡΑΞΗΣ» — Cash Drawer ΑΑ / ΒΒ split close. */}
      <ContinuePaymentDialog
        open={continuePayOpen}
        onOpenChange={setContinuePayOpen}
        tbl={tbl}
        payments={payments}
        onSettled={async (refreshed) => {
          if (refreshed?.id) updateTable(refreshed.id, refreshed);
          // Refresh tables grid so the just-closed table reverts to its
          // empty / pending status. loadAll re-fetches every dependency
          // and is a no-op cost compared to FTEXT close itself.
          try { await loadAll(); } catch { /* non-fatal */ }
          setCloseOpen(false);
          setSelectedTableId(null);
        }}
      />

      {/* Quick Note (Σχόλια) — one-off print to selected printers;
          does NOT mutate the table or its persisted comments. */}
      <QuickNoteDialog
        open={quickNoteOpen}
        onOpenChange={setQuickNoteOpen}
        table={tbl}
      />
    </div>
  );
}

function ChoiceButton({ icon: Icon, title, desc, onClick, disabled, testid, color }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full text-left rounded-lg border-2 border-slate-200 px-5 py-5 hover:border-slate-400 hover:shadow-lg transition-all disabled:opacity-50 disabled:pointer-events-none flex items-center gap-4 bg-white active:scale-[0.98]`}
    >
      <div className={`w-16 h-16 rounded-lg grid place-items-center text-white shrink-0 ${color}`}>
        <Icon size={32} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-lg">{title}</div>
        <div className="text-sm text-slate-500 mt-1">{desc}</div>
      </div>
    </button>
  );
}
